/**
 * 🗺️ Themed field maps (v1.39, per Javi). Four biomes — Forest, Desert,
 * Tundra, Hell — each painted as a PORTRAIT 887×1774 (1:2) canvas designed
 * for the phone: the whole painting fits the screen, no panning. Combat
 * nodes draw a random map from the ACT'S theme (one theme per act, never
 * repeated within a run — see actThemes in useRun.ts).
 *
 * Coordinates are percentages of the image (x right, y down, 0–100 both
 * axes). Every POI path starts at the map's home trailhead and follows the
 * trail Javi drew in magenta on the "- Copy" file next to each painting
 * (art/UI/<Theme>/, traced by art/_pipeline/trace_themes.py — POIs are the
 * network's 6 dead-end stroke tips). The old 4:3 landscape set lives on in
 * art/UI/ForestPC, retired from the game.
 */
import { rand } from '~/utils/random'

export type ForestTheme = 'forest' | 'desert' | 'tundra' | 'hell'

export const FOREST_THEMES: ForestTheme[] = ['forest', 'desert', 'tundra', 'hell']

/**
 * Per-theme dressing: the basecamp cutout that marks home on the map,
 * plus the PAGE TINT (v2.51, per Javi) — the biome's own darkness fills
 * the node screen behind the board and the roster, so every terrain
 * blends into its surroundings instead of a sky-blue page.
 */
export interface ThemeTint {
  /** Bottom of the page gradient (deepest shade). Kept well clear of
   * black (v2.52, per Javi: desert and tundra read "too close to
   * black") — the terrain should be recognisable behind the roster. */
  deep: string
  /** Top of the page gradient. */
  mid: string
  /** Glow bleeding down from behind the frozen ribbon. */
  glow: string
  /** Section-title ink that stays legible on the tint. */
  ink: string
}

export const THEME_INFO: Record<ForestTheme, { label: string; basecamp: string; tint: ThemeTint }> = {
  forest: {
    label: 'Forest',
    basecamp: '/ui/basecamp-forest.webp',
    tint: { deep: '#0c2013', mid: '#173a22', glow: 'rgba(46, 96, 60, 0.5)', ink: '#dff2e4' },
  },
  desert: {
    label: 'Desert',
    basecamp: '/ui/basecamp-desert.webp',
    tint: { deep: '#31200c', mid: '#5a3a15', glow: 'rgba(168, 108, 34, 0.45)', ink: '#f7e6c4' },
  },
  tundra: {
    label: 'Tundra',
    basecamp: '/ui/basecamp-tundra.webp',
    tint: { deep: '#152f45', mid: '#28506e', glow: 'rgba(74, 138, 184, 0.45)', ink: '#e4f2fb' },
  },
  hell: {
    label: 'Hell',
    basecamp: '/ui/basecamp-hell.webp',
    tint: { deep: '#260a07', mid: '#4a1a12', glow: 'rgba(176, 58, 26, 0.45)', ink: '#fbdcd2' },
  },
}

/** Width/height of every painting (887/1774): the x axis is half as long
 * per percent as y, so trail metrics weight dx by this. */
export const FOREST_ASPECT = 887 / 1774

export interface ForestPoi {
  /** Site position (marker anchor), % of image. */
  x: number
  y: number
  /** Trail from home to the site, % coords; path[0] === home. */
  path: [number, number][]
  /** 🚶 Trail length for TRAVEL TIME, pinned to the ORIGINAL 887×1774
   * composition (v2.63). Percent coordinates are relative to whatever
   * canvas a repaint happens to be framed on, so every re-crop silently
   * retuned how long teams walk — v2.62's uncropped canvases alone cut
   * the near POIs from 10/13 distance units to 7/8. Measured once on the
   * base canvas, travel now survives any amount of re-framing. */
  travelLength?: number
}

export interface ForestDef {
  id: string
  theme: ForestTheme
  image: string
  /** Optional motion layer with the exact framing of the static painting. */
  animation?: string
  /** Trailhead camp: teams set out from here; idle roster waits here. */
  home: { x: number; y: number }
  pois: ForestPoi[]
  /** Boss arenas only: where the boss stands — the painted CENTER RING
   * (v1.43, per Javi; the top gate is just its entrance). % coords. */
  bossSpot?: { x: number; y: number }
  /** 🧪 Extended canvases (v2.47): render full-width with vertical pan
   * instead of the centered 1:2 column — no page background visible. */
  bleed?: boolean
  /** CSS aspect-ratio of the painting when it isn't 887/1774. */
  canvasAspect?: string
  /** 🗺️ One-continuous-map anchors (v2.62), as a share of the canvas
   * HEIGHT: the topmost and bottom-most painted site (the camp counts as
   * both — see desert-3, whose trailhead is at the TOP). The banner rides
   * the terrain above the first and Your Team the terrain below the last;
   * neither may cross its anchor, or it clips a mission. */
  topAnchor?: number
  bottomAnchor?: number
  /** Seamless continuation of the painting's last rows. */
  tail?: string
  /** 🏟️ v6.28 boss arenas: the painted INNER RING (% coords, radii in % of
   * the canvas width / height). The boss lands at its center, the waiting
   * team stands on its rim — the `teamSide` half — and the boss walks in
   * from `gate`. `muster` is the rim point the team sets out from (and
   * walks back to), so the boss fight is a short dash, not a trek. */
  arena?: {
    ring: { x: number; y: number; rx: number; ry: number }
    gate: { x: number; y: number }
    teamSide: 'left' | 'bottom'
    muster: { x: number; y: number }
    /** The next ring out (as a multiple of the inner radii) for rosters
     * too big for one rim. Default 1.6. */
    spill?: number
  }
  /** 🧩 v6.28 puzzle halls: no basecamp painted at the trailhead. */
  noCamp?: boolean
}

export const FORESTS: ForestDef[] = [
  {
    id: 'forest-1',
    theme: 'forest',
    image: '/forests/forest-1.webp',
    home: { x: 55.0, y: 95.4 },
    pois: [
      { x: 26.2, y: 10.8, path: [[55.0, 95.4], [41.5, 95.8], [36.1, 93.3], [31.1, 92.7], [25.3, 89.7], [25.3, 86.8], [17.1, 82.8], [17.1, 81.2], [23.0, 78.7], [28.9, 77.3], [37.9, 72.8], [41.0, 70.3], [42.4, 68.1], [55.9, 60.7], [56.8, 60.0], [56.8, 58.9], [49.2, 54.6], [49.2, 51.4], [51.4, 50.1], [51.4, 48.5], [63.1, 42.8], [71.3, 42.2], [76.7, 39.2], [76.7, 37.7], [64.0, 31.3], [56.8, 29.8], [41.0, 28.6], [32.0, 24.6], [32.0, 22.8], [39.2, 19.2], [39.2, 17.4], [26.2, 10.8]] },
      { x: 75.8, y: 15.3, path: [[55.0, 95.4], [41.5, 95.8], [36.1, 93.3], [31.1, 92.7], [25.3, 89.7], [25.3, 86.8], [17.1, 82.8], [17.1, 81.2], [23.0, 78.7], [28.9, 77.3], [37.9, 72.8], [41.0, 70.3], [42.4, 68.1], [56.8, 60.0], [56.8, 58.9], [49.2, 54.6], [49.2, 51.4], [51.4, 50.1], [51.4, 48.5], [61.8, 43.3], [71.3, 42.2], [76.7, 39.2], [76.7, 37.7], [60.4, 28.6], [64.5, 26.2], [64.5, 22.3], [69.4, 19.8], [71.3, 17.6], [75.8, 15.3]] },
      { x: 26.6, y: 40.6, path: [[55.0, 95.4], [41.5, 95.8], [36.1, 93.3], [31.1, 92.7], [25.3, 89.7], [25.3, 86.8], [17.1, 82.8], [17.1, 81.2], [19.8, 79.8], [28.9, 77.3], [37.9, 72.8], [41.0, 70.3], [42.4, 68.1], [56.8, 60.0], [48.7, 56.1], [41.5, 55.5], [37.4, 53.4], [36.1, 51.0], [29.8, 46.7], [28.4, 44.4], [28.4, 41.5], [26.6, 40.6]] },
      { x: 73.1, y: 50.7, path: [[55.0, 95.4], [41.5, 95.8], [36.1, 93.3], [31.1, 92.7], [25.3, 89.7], [25.3, 86.8], [17.1, 82.8], [17.1, 81.2], [23.0, 78.7], [28.9, 77.3], [37.9, 72.8], [41.0, 70.3], [41.9, 68.5], [64.0, 57.3], [67.6, 53.4], [73.1, 50.7]] },
      { x: 21.2, y: 71.0, path: [[55.0, 95.4], [41.5, 95.8], [36.1, 93.3], [31.1, 92.7], [25.3, 89.7], [25.3, 86.8], [17.1, 82.8], [17.1, 81.2], [29.8, 75.3], [29.8, 74.6], [21.2, 71.0]] },
      { x: 52.8, y: 83.2, path: [[55.0, 95.4], [42.8, 95.8], [41.5, 95.2], [41.5, 92.7], [46.0, 90.4], [48.7, 85.2], [52.8, 83.2]] },
    ],
  },
  {
    id: 'forest-2',
    theme: 'forest',
    image: '/forests/forest-2.webp',
    home: { x: 22.1, y: 92.2 },
    pois: [
      { x: 77.1, y: 10.1, path: [[22.1, 92.2], [28.0, 94.5], [42.8, 94.9], [50.1, 97.0], [63.1, 97.0], [78.0, 89.5], [78.9, 88.2], [78.9, 83.7], [75.3, 81.8], [75.3, 79.6], [76.7, 78.2], [76.7, 75.8], [72.2, 73.5], [72.2, 68.3], [80.3, 63.8], [80.7, 57.3], [77.6, 55.0], [77.1, 51.4], [73.5, 49.6], [65.4, 48.9], [63.1, 47.8], [56.8, 46.9], [50.5, 43.7], [49.2, 42.2], [49.2, 38.1], [50.1, 37.2], [50.1, 35.2], [55.9, 31.1], [55.9, 28.4], [53.7, 27.1], [52.8, 23.9], [60.4, 20.3], [60.4, 18.7], [77.1, 10.1]] },
      { x: 30.7, y: 12.4, path: [[22.1, 92.2], [28.0, 94.5], [42.8, 94.9], [50.1, 97.0], [63.1, 97.0], [78.0, 89.5], [78.9, 88.2], [78.9, 83.7], [75.3, 81.8], [75.3, 79.6], [76.7, 78.2], [76.7, 75.8], [72.2, 73.5], [72.2, 68.3], [79.4, 64.7], [80.7, 62.9], [80.7, 57.3], [77.6, 55.0], [77.1, 51.4], [73.5, 49.6], [65.4, 48.9], [63.1, 47.8], [56.8, 46.9], [50.5, 43.7], [49.2, 42.2], [49.2, 38.1], [50.1, 37.2], [50.1, 35.2], [55.9, 31.1], [55.9, 28.4], [49.6, 24.4], [42.8, 23.4], [37.9, 21.0], [37.0, 18.3], [34.3, 14.4], [30.7, 12.4]] },
      { x: 25.3, y: 36.5, path: [[22.1, 92.2], [28.0, 94.5], [42.8, 94.9], [50.1, 97.0], [63.1, 97.0], [78.0, 89.5], [78.9, 88.2], [78.9, 83.7], [75.3, 81.8], [75.3, 79.6], [76.7, 78.2], [76.7, 75.8], [72.2, 73.5], [72.2, 68.3], [79.4, 64.7], [80.7, 62.9], [80.7, 57.3], [77.6, 55.0], [77.1, 51.4], [73.5, 49.6], [65.4, 48.9], [58.2, 46.9], [47.8, 46.9], [43.3, 48.3], [39.2, 48.5], [28.9, 43.7], [28.9, 38.3], [25.3, 36.5]] },
      { x: 70.8, y: 40.6, path: [[22.1, 92.2], [28.0, 94.5], [42.8, 94.9], [50.1, 97.0], [63.1, 97.0], [78.0, 89.5], [78.9, 88.2], [78.9, 83.7], [75.3, 81.8], [75.3, 79.6], [76.7, 78.2], [76.7, 75.8], [72.2, 73.5], [72.2, 68.3], [80.3, 63.8], [80.7, 57.3], [77.6, 55.0], [77.1, 51.4], [67.2, 47.4], [67.2, 42.6], [70.8, 40.6]] },
      { x: 46.9, y: 65.2, path: [[22.1, 92.2], [28.0, 94.5], [42.8, 94.9], [50.1, 97.0], [63.1, 97.0], [78.0, 89.5], [78.9, 88.2], [78.9, 85.5], [74.0, 83.0], [64.0, 81.4], [55.9, 78.9], [45.5, 73.7], [44.6, 71.9], [43.3, 71.3], [43.3, 67.0], [46.9, 65.2]] },
      { x: 51.4, y: 85.2, path: [[22.1, 92.2], [28.0, 94.5], [39.2, 94.7], [41.5, 93.6], [43.3, 90.9], [46.9, 88.6], [48.3, 86.8], [51.4, 85.2]] },
    ],
  },
  {
    id: 'forest-3',
    theme: 'forest',
    image: '/forests/forest-3.webp',
    home: { x: 58.6, y: 89.7 },
    pois: [
      { x: 79.4, y: 12.2, path: [[58.6, 89.7], [58.6, 90.4], [50.1, 95.2], [45.5, 95.2], [33.4, 89.1], [32.5, 88.2], [32.5, 85.9], [41.0, 82.1], [41.5, 80.7], [53.2, 74.9], [61.3, 73.3], [66.3, 70.6], [65.8, 69.4], [61.3, 68.8], [56.8, 66.5], [53.2, 63.1], [53.2, 60.9], [65.4, 54.8], [72.2, 54.1], [77.1, 51.6], [77.6, 50.1], [74.0, 48.5], [61.3, 46.9], [55.9, 44.2], [54.1, 41.3], [50.1, 38.8], [50.1, 34.3], [58.6, 29.5], [58.6, 27.3], [77.1, 19.4], [78.5, 17.1], [78.5, 12.6], [79.4, 12.2]] },
      { x: 32.5, y: 14.2, path: [[58.6, 89.7], [58.6, 90.4], [50.1, 95.2], [45.5, 95.2], [33.4, 89.1], [32.5, 88.2], [32.5, 85.9], [41.0, 82.1], [41.5, 80.7], [53.2, 74.9], [61.3, 73.3], [65.8, 71.0], [66.3, 69.7], [61.3, 68.8], [56.8, 66.5], [53.2, 63.1], [53.2, 60.9], [65.4, 54.8], [72.2, 54.1], [77.1, 51.6], [77.6, 50.1], [74.0, 48.5], [61.3, 46.9], [55.9, 44.2], [54.1, 41.3], [50.1, 38.8], [50.1, 34.3], [53.2, 32.2], [53.2, 30.7], [37.0, 23.0], [37.0, 18.9], [35.6, 18.0], [34.7, 15.3], [32.5, 14.2]] },
      { x: 70.8, y: 40.1, path: [[58.6, 89.7], [58.6, 90.4], [50.1, 95.2], [45.5, 95.2], [33.4, 89.1], [32.5, 88.2], [32.5, 85.9], [41.0, 82.1], [41.5, 80.7], [53.2, 74.9], [61.3, 73.3], [66.3, 70.6], [65.8, 69.4], [61.3, 68.8], [56.8, 66.5], [53.2, 63.1], [53.2, 60.9], [65.4, 54.8], [72.2, 54.1], [77.6, 51.2], [77.6, 50.1], [74.9, 48.7], [70.3, 48.3], [65.4, 45.8], [65.4, 42.8], [70.8, 40.1]] },
      { x: 22.5, y: 45.1, path: [[58.6, 89.7], [58.6, 90.4], [50.1, 95.2], [45.5, 95.2], [32.5, 88.2], [32.5, 85.9], [41.0, 82.1], [41.5, 80.7], [53.2, 74.9], [61.3, 73.3], [65.8, 71.0], [66.3, 69.7], [61.3, 68.8], [56.8, 66.5], [53.2, 63.1], [53.2, 60.0], [54.1, 58.9], [47.4, 55.7], [38.3, 54.6], [32.0, 51.4], [30.2, 48.9], [22.5, 45.1]] },
      { x: 77.6, y: 64.0, path: [[58.6, 89.7], [58.6, 90.4], [50.1, 95.2], [45.5, 95.2], [33.4, 89.1], [32.5, 88.2], [32.5, 85.9], [41.0, 82.1], [41.5, 80.7], [53.2, 74.9], [61.3, 73.3], [65.8, 71.0], [66.3, 69.7], [77.6, 64.0]] },
      { x: 29.3, y: 72.2, path: [[58.6, 89.7], [58.6, 90.4], [50.1, 95.2], [45.5, 95.2], [33.4, 89.1], [32.5, 88.2], [32.5, 85.9], [41.5, 81.4], [33.4, 77.8], [30.7, 72.8], [29.3, 72.2]] },
    ],
  },
  {
    id: 'forest-4',
    theme: 'forest',
    image: '/forests/forest-4.webp',
    home: { x: 56.8, y: 92.9 },
    pois: [
      { x: 70.8, y: 15.8, path: [[56.8, 92.9], [59.5, 92.9], [62.7, 94.5], [70.8, 94.7], [72.2, 94.0], [72.2, 91.8], [88.8, 83.9], [88.8, 80.3], [78.9, 75.3], [78.9, 72.6], [81.2, 71.5], [81.2, 70.1], [67.6, 66.5], [59.5, 66.1], [41.5, 57.9], [32.5, 57.3], [24.8, 53.4], [24.8, 52.3], [35.2, 50.3], [48.3, 42.8], [48.7, 35.6], [53.2, 33.4], [53.2, 30.9], [50.5, 29.5], [50.5, 28.0], [59.1, 25.5], [65.4, 22.5], [68.5, 18.9], [69.0, 16.7], [70.8, 15.8]] },
      { x: 23.4, y: 16.7, path: [[56.8, 92.9], [59.5, 92.9], [62.7, 94.5], [70.8, 94.7], [72.2, 94.0], [72.2, 91.8], [88.8, 83.9], [88.8, 80.3], [78.9, 75.3], [78.9, 72.6], [81.2, 71.5], [81.2, 70.1], [79.4, 69.2], [67.6, 66.5], [59.5, 66.1], [42.8, 58.4], [32.5, 57.3], [24.8, 53.4], [24.8, 52.3], [25.7, 51.9], [35.2, 50.3], [48.3, 42.8], [48.7, 35.6], [53.2, 33.4], [53.2, 30.9], [47.4, 27.3], [36.5, 24.4], [31.6, 21.9], [31.6, 20.7], [23.4, 16.7]] },
      { x: 23.9, y: 41.7, path: [[56.8, 92.9], [59.5, 92.9], [62.7, 94.5], [70.8, 94.7], [72.2, 94.0], [72.2, 91.8], [88.8, 83.9], [88.8, 80.3], [78.9, 75.3], [78.9, 72.6], [81.2, 71.5], [81.2, 70.1], [79.4, 69.2], [67.6, 66.5], [59.5, 66.1], [42.8, 58.4], [32.5, 57.3], [24.8, 53.4], [24.8, 52.3], [25.7, 51.9], [30.2, 51.0], [35.2, 48.5], [28.4, 45.1], [27.1, 43.3], [23.9, 41.7]] },
      { x: 66.3, y: 46.7, path: [[56.8, 92.9], [59.5, 92.9], [62.7, 94.5], [70.8, 94.7], [72.2, 94.0], [72.2, 91.8], [88.8, 83.9], [88.8, 80.3], [78.9, 75.3], [78.9, 72.6], [81.2, 71.5], [81.2, 70.1], [67.6, 66.5], [59.5, 66.1], [46.4, 59.5], [46.9, 58.6], [51.4, 57.9], [56.4, 55.5], [58.2, 53.7], [59.5, 50.3], [66.3, 46.7]] },
      { x: 26.2, y: 65.6, path: [[56.8, 92.9], [59.5, 92.9], [62.7, 94.5], [70.8, 94.7], [72.2, 94.0], [72.2, 91.8], [67.2, 90.0], [56.4, 88.8], [45.1, 83.2], [45.1, 80.5], [47.4, 79.4], [47.4, 78.7], [39.7, 74.9], [34.3, 69.7], [26.2, 65.6]] },
      { x: 69.0, y: 82.5, path: [[56.8, 92.9], [59.5, 92.9], [62.7, 94.5], [70.8, 94.7], [72.2, 94.0], [72.2, 91.8], [67.2, 90.0], [56.4, 88.8], [45.1, 83.2], [45.1, 82.3], [51.9, 79.6], [62.2, 79.1], [69.0, 82.5]] },
    ],
  },
  {
    id: 'desert-1',
    theme: 'desert',
    image: '/forests/desert-1.webp',
    home: { x: 31.1, y: 97.0 },
    pois: [
      { x: 28.4, y: 12.0, path: [[31.1, 97.0], [36.1, 97.0], [42.8, 98.1], [43.7, 97.6], [43.7, 94.9], [35.6, 90.9], [35.2, 89.3], [23.4, 83.4], [22.1, 79.1], [27.1, 76.9], [37.9, 75.3], [47.4, 70.6], [52.3, 69.4], [59.5, 65.8], [58.6, 59.5], [49.6, 55.5], [49.2, 54.3], [46.0, 52.8], [38.8, 49.8], [38.3, 47.8], [51.0, 41.5], [51.4, 40.4], [55.9, 38.1], [60.4, 37.9], [65.8, 35.2], [66.3, 32.2], [63.1, 30.7], [60.0, 30.2], [51.0, 25.7], [47.8, 25.3], [41.9, 22.3], [41.5, 19.8], [36.1, 17.1], [35.2, 15.3], [28.4, 12.0]] },
      { x: 80.7, y: 22.8, path: [[31.1, 97.0], [36.1, 97.0], [42.8, 98.1], [43.7, 97.6], [43.7, 94.9], [35.6, 90.9], [35.2, 89.3], [23.4, 83.4], [22.1, 79.1], [27.1, 76.9], [37.9, 75.3], [47.4, 70.6], [52.3, 69.4], [59.5, 65.8], [58.6, 59.5], [49.6, 55.5], [49.2, 54.3], [46.0, 52.8], [38.8, 49.8], [38.3, 47.8], [51.0, 41.5], [51.4, 40.4], [55.9, 38.1], [60.4, 37.9], [65.8, 35.2], [66.3, 30.0], [80.7, 22.8]] },
      { x: 32.0, y: 35.2, path: [[31.1, 97.0], [36.1, 97.0], [42.8, 98.1], [43.7, 97.6], [43.7, 94.9], [35.6, 90.9], [35.2, 89.3], [23.4, 83.4], [22.1, 79.1], [27.1, 76.9], [37.9, 75.3], [47.4, 70.6], [52.3, 69.4], [59.5, 65.8], [58.6, 59.5], [49.6, 55.5], [49.2, 54.3], [46.0, 52.8], [38.8, 49.8], [38.3, 46.2], [40.6, 45.1], [40.6, 42.6], [37.4, 40.6], [34.7, 36.5], [32.0, 35.2]] },
      { x: 70.3, y: 50.1, path: [[31.1, 97.0], [36.1, 97.0], [42.8, 98.1], [43.7, 97.6], [43.7, 94.9], [35.6, 90.9], [35.2, 89.3], [23.4, 83.4], [22.1, 79.1], [27.1, 76.9], [37.9, 75.3], [47.4, 70.6], [52.3, 69.4], [59.5, 65.8], [58.6, 59.8], [67.6, 55.2], [68.5, 51.0], [70.3, 50.1]] },
      { x: 29.3, y: 63.4, path: [[31.1, 97.0], [36.1, 97.0], [42.8, 98.1], [43.7, 97.6], [43.7, 94.9], [35.6, 90.9], [35.2, 89.3], [23.4, 83.4], [22.1, 79.1], [26.2, 77.1], [37.0, 75.3], [42.8, 72.4], [42.8, 71.0], [29.3, 63.4]] },
      { x: 62.7, y: 84.6, path: [[31.1, 97.0], [36.1, 97.0], [42.8, 98.1], [43.7, 97.6], [43.7, 95.2], [55.9, 89.3], [62.7, 84.6]] },
    ],
  },
  {
    id: 'desert-2',
    theme: 'desert',
    image: '/forests/desert-2.webp',
    home: { x: 66.3, y: 94.9 },
    pois: [
      { x: 36.5, y: 11.5, path: [[66.3, 94.9], [46.0, 95.8], [22.5, 83.7], [22.1, 79.4], [26.6, 77.1], [50.1, 71.5], [59.5, 66.5], [59.5, 62.9], [58.2, 62.0], [58.2, 60.4], [54.6, 58.6], [49.2, 57.3], [41.0, 53.2], [40.6, 50.3], [44.2, 48.5], [47.8, 47.8], [55.9, 43.7], [56.8, 42.8], [57.7, 37.9], [52.8, 36.1], [49.6, 35.9], [35.2, 27.7], [35.2, 25.9], [38.3, 24.4], [43.3, 23.4], [49.6, 20.3], [49.6, 18.0], [36.5, 11.5]] },
      { x: 71.3, y: 27.1, path: [[66.3, 94.9], [46.0, 95.8], [23.9, 84.8], [22.5, 83.7], [22.1, 79.4], [29.3, 76.2], [50.1, 71.5], [59.5, 66.5], [59.5, 62.9], [58.2, 62.0], [58.2, 60.4], [54.6, 58.6], [49.2, 57.3], [41.0, 53.2], [40.6, 50.3], [44.2, 48.5], [47.8, 47.8], [55.9, 43.7], [56.8, 42.8], [57.3, 39.5], [63.6, 36.1], [65.4, 32.2], [69.4, 28.0], [71.3, 27.1]] },
      { x: 30.7, y: 40.1, path: [[66.3, 94.9], [46.0, 95.8], [22.5, 83.7], [22.1, 79.4], [26.6, 77.1], [50.1, 71.5], [59.5, 66.5], [59.5, 62.9], [58.2, 62.0], [58.2, 60.4], [54.6, 58.6], [49.2, 57.3], [41.0, 53.2], [40.6, 50.3], [38.3, 49.2], [35.2, 46.0], [34.7, 42.2], [30.7, 40.1]] },
      { x: 71.7, y: 51.2, path: [[66.3, 94.9], [46.0, 95.8], [23.9, 84.8], [22.5, 83.7], [22.1, 79.4], [29.3, 76.2], [50.1, 71.5], [59.1, 67.0], [59.5, 62.9], [58.2, 62.0], [58.2, 59.3], [64.0, 56.1], [65.4, 54.3], [71.7, 51.2]] },
      { x: 30.2, y: 64.5, path: [[66.3, 94.9], [46.0, 95.8], [23.0, 84.1], [22.1, 79.4], [29.3, 76.2], [33.8, 75.5], [40.1, 72.4], [40.1, 70.8], [30.2, 64.5]] },
      { x: 60.0, y: 83.9, path: [[66.3, 94.9], [48.7, 95.8], [39.2, 91.1], [45.5, 88.4], [52.8, 88.2], [60.0, 83.9]] },
    ],
  },
  {
    id: 'desert-3',
    theme: 'desert',
    image: '/forests/desert-3.webp',
    home: { x: 68.1, y: 10.6 },
    pois: [
      { x: 29.8, y: 12.4, path: [[68.1, 10.6], [56.8, 16.0], [46.4, 19.2], [40.6, 19.2], [29.8, 12.4]] },
      { x: 76.2, y: 24.1, path: [[68.1, 10.6], [41.0, 24.4], [40.6, 26.4], [43.7, 28.0], [55.0, 30.9], [61.8, 31.3], [76.2, 24.1]] },
      { x: 32.0, y: 35.2, path: [[68.1, 10.6], [40.6, 24.8], [40.6, 26.4], [43.7, 28.0], [51.4, 29.5], [63.1, 35.4], [63.1, 37.4], [54.1, 41.9], [46.9, 43.1], [32.0, 35.2]] },
      { x: 77.6, y: 54.1, path: [[68.1, 10.6], [40.6, 24.8], [40.6, 26.4], [43.7, 28.0], [51.4, 29.5], [63.1, 35.4], [63.1, 37.4], [54.1, 41.9], [46.9, 44.0], [32.0, 51.9], [32.0, 53.0], [34.7, 54.1], [42.4, 55.0], [50.1, 57.9], [59.1, 59.8], [64.5, 59.8], [77.6, 54.1]] },
      { x: 27.1, y: 64.9, path: [[68.1, 10.6], [40.6, 24.8], [40.6, 26.4], [43.7, 28.0], [51.4, 29.5], [63.1, 35.4], [63.1, 37.4], [54.1, 41.9], [49.2, 42.8], [32.0, 51.9], [32.0, 53.0], [33.8, 53.9], [45.1, 55.7], [54.6, 60.0], [56.8, 61.1], [56.8, 64.3], [51.0, 67.0], [34.3, 67.0], [27.1, 64.9]] },
      { x: 69.4, y: 86.6, path: [[68.1, 10.6], [40.6, 24.8], [40.6, 26.4], [43.7, 28.0], [51.4, 29.5], [63.1, 35.4], [63.1, 37.4], [54.1, 41.9], [49.2, 42.8], [32.0, 51.9], [32.0, 53.0], [33.8, 53.9], [45.1, 55.7], [56.8, 61.1], [56.8, 65.8], [53.7, 67.4], [53.7, 69.4], [55.0, 70.8], [50.5, 72.6], [44.2, 73.7], [37.4, 77.1], [37.0, 79.6], [27.1, 84.6], [27.1, 87.7], [37.4, 92.0], [56.4, 93.1], [69.4, 86.6]] },
    ],
  },
  {
    id: 'desert-4',
    theme: 'desert',
    image: '/forests/desert-4.webp',
    home: { x: 21.2, y: 94.0 },
    pois: [
      { x: 28.0, y: 17.1, path: [[21.2, 94.0], [40.6, 94.7], [48.3, 92.4], [55.5, 88.8], [58.6, 86.6], [59.5, 85.0], [59.5, 83.2], [55.9, 80.7], [55.9, 78.0], [59.5, 75.8], [39.2, 67.0], [33.4, 64.0], [31.6, 62.0], [31.6, 59.5], [41.5, 54.6], [47.4, 53.4], [53.2, 50.5], [54.6, 48.9], [54.6, 46.4], [53.2, 45.5], [53.2, 44.4], [43.3, 39.5], [42.8, 38.1], [36.1, 34.5], [35.6, 32.0], [43.7, 28.0], [43.7, 27.1], [37.9, 24.1], [36.1, 21.4], [28.0, 17.1]] },
      { x: 70.8, y: 17.6, path: [[21.2, 94.0], [40.6, 94.7], [48.3, 92.4], [55.5, 88.8], [59.1, 86.1], [59.5, 83.2], [56.4, 81.4], [55.9, 78.0], [59.5, 75.8], [39.2, 67.0], [33.4, 64.0], [31.6, 62.0], [31.6, 59.5], [41.5, 54.6], [47.4, 53.4], [53.2, 50.5], [54.6, 48.9], [54.6, 46.4], [53.2, 45.5], [53.2, 44.4], [43.3, 39.5], [42.8, 38.1], [37.0, 35.2], [35.6, 33.8], [35.6, 32.0], [43.7, 28.0], [43.7, 26.4], [48.7, 23.9], [60.9, 21.9], [70.8, 17.6]] },
      { x: 72.6, y: 39.9, path: [[21.2, 94.0], [40.6, 94.7], [48.3, 92.4], [55.5, 88.8], [59.1, 86.1], [59.5, 83.2], [56.4, 81.4], [55.9, 78.0], [59.5, 75.8], [33.4, 64.0], [31.6, 62.0], [31.6, 59.5], [41.5, 54.6], [47.4, 53.4], [53.2, 50.5], [54.6, 48.9], [54.6, 46.4], [53.2, 45.5], [53.2, 44.6], [59.5, 41.5], [59.5, 39.7], [64.5, 37.2], [69.9, 36.5], [70.8, 37.0], [70.8, 39.0], [72.6, 39.9]] },
      { x: 27.1, y: 46.4, path: [[21.2, 94.0], [40.6, 94.7], [48.3, 92.4], [55.5, 88.8], [58.6, 86.6], [59.5, 85.0], [59.5, 83.2], [55.9, 80.7], [55.9, 78.0], [59.5, 75.8], [39.2, 67.0], [32.0, 62.7], [31.6, 58.2], [35.6, 55.9], [36.1, 52.8], [32.9, 49.4], [27.1, 46.4]] },
      { x: 67.6, y: 63.1, path: [[21.2, 94.0], [40.6, 94.7], [48.3, 92.4], [55.5, 88.8], [58.6, 86.6], [59.5, 85.0], [59.5, 83.2], [55.9, 80.7], [55.9, 78.0], [59.5, 76.2], [59.5, 74.6], [53.7, 71.7], [53.7, 70.1], [67.6, 63.1]] },
      { x: 28.4, y: 83.0, path: [[21.2, 94.0], [37.0, 94.7], [40.6, 92.9], [40.6, 90.9], [36.5, 87.0], [28.4, 83.0]] },
    ],
  },
  {
    id: 'tundra-1',
    theme: 'tundra',
    image: '/forests/tundra-1.webp',
    home: { x: 23.0, y: 94.5 },
    pois: [
      { x: 30.2, y: 11.7, path: [[23.0, 94.5], [32.9, 94.0], [32.9, 92.9], [26.6, 89.7], [27.5, 83.0], [32.5, 80.5], [38.3, 78.9], [52.3, 77.1], [64.9, 70.6], [64.9, 69.2], [57.3, 65.4], [57.3, 63.1], [50.5, 59.8], [47.4, 59.5], [41.5, 56.6], [40.6, 54.6], [40.6, 53.4], [55.9, 46.2], [56.4, 40.6], [63.6, 37.0], [64.5, 35.2], [62.7, 34.3], [58.2, 33.8], [39.7, 23.7], [39.7, 21.2], [41.5, 20.3], [41.5, 18.5], [30.2, 11.7]] },
      { x: 70.3, y: 27.1, path: [[23.0, 94.5], [32.9, 94.0], [32.9, 92.9], [26.6, 89.7], [27.5, 83.0], [32.5, 80.5], [38.3, 78.9], [52.3, 77.1], [64.9, 70.6], [64.9, 69.2], [57.3, 65.4], [57.3, 63.1], [50.5, 59.8], [47.4, 59.5], [41.5, 56.6], [40.6, 53.4], [55.9, 46.2], [56.4, 40.6], [63.6, 37.0], [64.5, 36.1], [64.5, 31.8], [66.7, 30.7], [68.5, 28.0], [70.3, 27.1]] },
      { x: 31.6, y: 44.0, path: [[23.0, 94.5], [32.9, 94.0], [32.9, 92.9], [26.6, 89.7], [27.5, 83.0], [32.5, 80.5], [38.3, 78.9], [52.3, 77.1], [64.9, 70.6], [64.9, 69.2], [57.3, 65.4], [57.3, 63.1], [50.5, 59.8], [47.4, 59.5], [41.5, 56.6], [40.6, 54.6], [40.6, 51.6], [41.9, 50.7], [41.9, 49.8], [31.6, 44.0]] },
      { x: 73.5, y: 53.9, path: [[23.0, 94.5], [32.9, 94.0], [32.9, 92.9], [26.6, 89.7], [27.5, 83.0], [32.5, 80.5], [38.3, 78.9], [52.3, 77.1], [64.5, 71.0], [64.9, 69.2], [57.3, 65.4], [57.3, 64.3], [63.6, 62.2], [69.9, 59.1], [71.3, 57.3], [71.7, 54.8], [73.5, 53.9]] },
      { x: 27.1, y: 69.9, path: [[23.0, 94.5], [32.9, 94.0], [32.9, 92.9], [26.6, 89.7], [26.6, 86.4], [27.5, 85.9], [27.5, 82.1], [35.6, 78.0], [35.6, 76.2], [30.7, 72.8], [30.2, 71.5], [27.1, 69.9]] },
      { x: 62.2, y: 87.7, path: [[23.0, 94.5], [46.9, 96.1], [62.2, 87.7]] },
    ],
  },
  {
    id: 'tundra-2',
    theme: 'tundra',
    image: '/forests/tundra-2.webp',
    home: { x: 34.7, y: 94.3 },
    pois: [
      { x: 28.0, y: 10.6, path: [[34.7, 94.3], [31.1, 94.3], [28.4, 92.9], [21.6, 92.9], [15.8, 89.7], [14.4, 85.0], [20.3, 82.1], [36.1, 78.0], [40.1, 76.2], [51.9, 74.4], [64.0, 74.2], [68.5, 71.9], [69.9, 69.7], [78.5, 65.4], [80.7, 63.4], [65.4, 58.2], [57.7, 56.8], [45.1, 50.5], [42.8, 50.3], [37.4, 47.6], [37.4, 46.9], [57.7, 36.1], [58.2, 33.8], [36.1, 23.2], [36.1, 18.0], [30.7, 13.1], [30.2, 11.7], [28.0, 10.6]] },
      { x: 74.9, y: 14.0, path: [[34.7, 94.3], [31.1, 94.3], [28.4, 92.9], [21.6, 92.9], [15.8, 89.7], [14.4, 85.0], [20.3, 82.1], [36.1, 78.0], [40.1, 76.2], [51.9, 74.4], [64.0, 74.2], [68.5, 71.9], [69.9, 69.7], [80.7, 63.8], [78.5, 62.2], [65.4, 58.2], [57.7, 56.8], [45.1, 50.5], [42.8, 50.3], [37.4, 47.6], [37.4, 46.9], [57.7, 36.1], [58.2, 33.8], [36.1, 23.2], [36.1, 22.1], [39.2, 20.5], [43.7, 20.3], [49.2, 18.5], [63.1, 18.9], [74.9, 14.0]] },
      { x: 27.5, y: 36.8, path: [[34.7, 94.3], [31.1, 94.3], [28.4, 92.9], [21.6, 92.9], [15.8, 89.7], [14.4, 85.0], [20.3, 82.1], [36.1, 78.0], [40.1, 76.2], [51.9, 74.4], [64.0, 74.2], [68.5, 71.9], [69.9, 69.7], [78.5, 65.4], [80.7, 63.4], [65.4, 58.2], [57.7, 56.8], [45.1, 50.5], [42.8, 50.3], [38.3, 48.0], [37.4, 47.6], [37.4, 46.7], [32.9, 44.2], [32.0, 39.2], [27.5, 36.8]] },
      { x: 77.1, y: 48.7, path: [[34.7, 94.3], [31.1, 94.3], [28.4, 92.9], [21.6, 92.9], [15.8, 89.7], [14.4, 85.0], [20.3, 82.1], [36.1, 78.0], [40.1, 76.2], [51.9, 74.4], [64.0, 74.2], [68.5, 71.9], [69.9, 69.7], [80.7, 63.8], [69.0, 57.5], [69.0, 55.5], [74.0, 52.8], [74.9, 49.8], [77.1, 48.7]] },
      { x: 46.4, y: 62.5, path: [[34.7, 94.3], [31.1, 94.3], [28.4, 92.9], [21.6, 92.9], [15.8, 89.7], [14.4, 85.0], [20.3, 82.1], [29.3, 79.6], [37.0, 75.8], [37.4, 73.7], [35.2, 72.6], [35.2, 69.7], [41.5, 66.5], [46.4, 62.5]] },
      { x: 56.4, y: 87.9, path: [[34.7, 94.3], [37.4, 94.3], [40.6, 92.7], [44.2, 92.7], [56.4, 87.9]] },
    ],
  },
  {
    id: 'tundra-3',
    theme: 'tundra',
    image: '/forests/tundra-3.webp',
    home: { x: 22.5, y: 92.4 },
    pois: [
      { x: 31.1, y: 11.5, path: [[22.5, 92.4], [29.3, 92.4], [32.5, 90.2], [23.0, 85.0], [22.5, 82.3], [33.8, 79.4], [51.9, 70.3], [52.8, 68.5], [52.8, 67.9], [46.4, 64.7], [46.4, 63.8], [32.9, 57.7], [32.0, 56.6], [32.0, 54.6], [41.9, 50.1], [47.4, 49.2], [53.7, 46.0], [54.1, 43.1], [64.5, 37.9], [64.5, 37.0], [60.9, 35.2], [51.9, 32.2], [43.3, 28.0], [33.8, 27.1], [28.9, 24.6], [28.9, 23.2], [33.8, 22.1], [38.8, 19.6], [38.8, 18.0], [36.1, 16.7], [35.6, 14.0], [31.1, 11.5]] },
      { x: 72.6, y: 23.7, path: [[22.5, 92.4], [29.3, 92.4], [32.5, 90.2], [23.0, 85.0], [22.5, 82.3], [33.8, 79.4], [51.9, 70.3], [52.8, 68.5], [52.8, 67.9], [46.4, 64.7], [46.4, 63.8], [32.9, 57.7], [32.0, 56.6], [32.0, 54.6], [41.9, 50.1], [47.4, 49.2], [53.7, 46.0], [54.1, 43.1], [64.5, 37.9], [64.5, 35.4], [60.4, 33.4], [60.4, 31.3], [63.6, 29.8], [65.4, 27.3], [72.6, 23.7]] },
      { x: 26.6, y: 39.9, path: [[22.5, 92.4], [29.3, 92.4], [32.5, 90.2], [23.0, 85.0], [22.5, 82.3], [33.8, 79.4], [51.9, 70.3], [52.8, 68.5], [52.8, 67.9], [46.4, 64.7], [46.4, 63.8], [32.5, 57.3], [32.0, 53.0], [38.3, 49.8], [38.3, 48.5], [34.3, 46.4], [32.5, 42.8], [26.6, 39.9]] },
      { x: 67.6, y: 64.0, path: [[22.5, 92.4], [29.3, 92.4], [32.5, 90.2], [23.0, 85.0], [22.5, 82.3], [29.3, 80.0], [35.2, 79.1], [56.4, 68.5], [59.1, 68.3], [67.6, 64.0]] },
      { x: 24.8, y: 67.6, path: [[22.5, 92.4], [29.3, 92.4], [32.5, 90.2], [23.0, 85.0], [22.5, 82.3], [33.4, 79.4], [38.3, 76.9], [38.3, 76.2], [33.4, 73.7], [31.1, 71.0], [24.8, 67.6]] },
      { x: 65.4, y: 87.9, path: [[22.5, 92.4], [32.5, 93.1], [40.6, 95.2], [46.0, 95.2], [51.0, 94.3], [65.4, 87.9]] },
    ],
  },
  {
    id: 'hell-1',
    theme: 'hell',
    image: '/forests/hell-1.webp',
    home: { x: 15.3, y: 93.3 },
    pois: [
      { x: 29.8, y: 12.0, path: [[15.3, 93.3], [21.2, 95.6], [32.5, 95.8], [38.3, 98.1], [59.1, 98.3], [74.0, 92.0], [76.2, 87.7], [76.2, 84.8], [71.7, 82.5], [51.4, 76.4], [41.9, 75.3], [32.9, 70.8], [31.1, 68.8], [31.1, 67.0], [34.3, 65.4], [39.7, 64.5], [48.7, 60.0], [51.9, 57.0], [56.4, 54.6], [56.4, 52.1], [43.3, 45.3], [43.3, 42.8], [50.5, 37.2], [51.0, 34.3], [30.7, 25.0], [30.7, 24.4], [37.0, 21.0], [37.0, 16.0], [29.8, 12.0]] },
      { x: 70.8, y: 15.1, path: [[15.3, 93.3], [21.2, 95.6], [32.5, 95.8], [38.3, 98.1], [59.1, 98.3], [67.6, 95.2], [74.9, 91.1], [76.2, 87.7], [76.2, 84.8], [71.7, 82.5], [61.8, 79.4], [57.3, 78.7], [53.2, 76.9], [41.9, 75.3], [32.9, 70.8], [31.1, 68.8], [31.1, 67.0], [34.7, 65.2], [39.7, 64.5], [48.7, 60.0], [51.9, 57.0], [56.4, 54.6], [56.4, 52.1], [43.3, 45.3], [43.3, 42.8], [51.0, 36.5], [51.0, 33.6], [49.2, 32.7], [49.2, 30.7], [57.3, 25.9], [61.8, 19.6], [70.8, 15.1]] },
      { x: 24.4, y: 34.7, path: [[15.3, 93.3], [21.2, 95.6], [32.5, 95.8], [38.3, 98.1], [59.1, 98.3], [74.0, 92.0], [76.2, 87.7], [76.2, 84.8], [71.7, 82.5], [51.4, 76.4], [41.9, 75.3], [32.9, 70.8], [31.6, 69.4], [31.1, 67.0], [34.7, 65.2], [39.7, 64.5], [48.7, 60.0], [51.9, 57.0], [56.4, 54.6], [56.4, 53.0], [45.5, 47.6], [36.1, 45.5], [28.9, 41.9], [28.4, 36.8], [24.4, 34.7]] },
      { x: 30.2, y: 57.3, path: [[15.3, 93.3], [21.2, 95.6], [32.5, 95.8], [38.3, 98.1], [59.1, 98.3], [68.1, 94.9], [74.0, 92.0], [75.3, 90.2], [76.2, 84.8], [71.7, 82.5], [51.4, 76.4], [41.9, 75.3], [32.9, 70.8], [31.1, 68.8], [31.1, 65.8], [36.1, 63.4], [36.1, 60.9], [30.2, 57.3]] },
      { x: 70.8, y: 65.4, path: [[15.3, 93.3], [21.2, 95.6], [32.5, 95.8], [38.3, 98.1], [59.1, 98.3], [74.0, 92.0], [76.2, 87.7], [76.2, 84.8], [71.7, 82.5], [65.4, 80.9], [56.8, 76.7], [56.8, 73.7], [59.1, 72.6], [60.4, 70.6], [70.8, 65.4]] },
      { x: 44.2, y: 85.5, path: [[15.3, 93.3], [17.6, 93.3], [21.2, 94.9], [31.6, 95.4], [34.7, 93.8], [39.2, 87.9], [44.2, 85.5]] },
    ],
  },
  {
    id: 'hell-2',
    theme: 'hell',
    image: '/forests/hell-2.webp',
    home: { x: 30.7, y: 94.3 },
    pois: [
      { x: 74.9, y: 12.4, path: [[30.7, 94.3], [25.3, 94.5], [20.3, 91.1], [20.3, 86.4], [24.4, 83.4], [24.8, 79.1], [40.6, 74.4], [45.1, 74.4], [60.0, 67.0], [61.3, 65.6], [61.3, 64.5], [47.4, 56.6], [46.0, 48.3], [58.2, 42.2], [58.2, 37.9], [75.3, 30.4], [74.9, 29.5], [70.8, 28.4], [64.5, 25.0], [64.5, 21.9], [69.0, 19.6], [70.3, 17.6], [70.8, 14.9], [74.9, 12.4]] },
      { x: 29.3, y: 14.9, path: [[30.7, 94.3], [25.3, 94.5], [20.3, 91.1], [20.3, 86.4], [24.4, 83.4], [24.8, 79.1], [40.6, 74.4], [45.1, 74.4], [60.0, 67.0], [61.3, 65.6], [61.3, 64.5], [47.4, 56.6], [46.0, 48.3], [58.2, 42.2], [58.2, 37.9], [66.3, 33.8], [69.9, 33.1], [75.3, 30.4], [75.3, 29.8], [72.2, 28.4], [64.0, 26.8], [52.3, 26.8], [49.2, 25.5], [42.8, 24.8], [37.9, 22.3], [35.2, 18.7], [29.3, 14.9]] },
      { x: 29.3, y: 40.4, path: [[30.7, 94.3], [25.3, 94.5], [20.3, 91.1], [20.3, 86.4], [24.4, 83.4], [24.8, 79.1], [40.6, 74.4], [45.1, 74.4], [60.0, 67.0], [61.3, 65.6], [61.3, 64.5], [47.8, 57.0], [46.9, 55.9], [46.9, 52.5], [41.9, 50.1], [38.8, 50.1], [34.3, 47.8], [32.5, 42.2], [29.3, 40.4]] },
      { x: 74.0, y: 55.2, path: [[30.7, 94.3], [25.3, 94.5], [20.3, 91.1], [20.3, 86.4], [24.4, 83.4], [24.8, 79.1], [40.6, 74.4], [45.1, 74.4], [60.0, 67.0], [61.3, 65.6], [61.3, 61.6], [74.0, 55.2]] },
      { x: 28.9, y: 67.4, path: [[30.7, 94.3], [25.3, 94.5], [20.3, 91.1], [20.3, 86.4], [24.4, 83.4], [24.8, 79.1], [36.1, 74.2], [36.1, 72.4], [33.4, 69.7], [28.9, 67.4]] },
      { x: 74.9, y: 78.5, path: [[30.7, 94.3], [34.3, 94.3], [37.0, 92.9], [43.7, 92.9], [64.0, 82.8], [69.0, 81.4], [74.9, 78.5]] },
    ],
  },
  {
    id: 'hell-3',
    theme: 'hell',
    image: '/forests/hell-3.webp',
    home: { x: 35.6, y: 94.7 },
    pois: [
      { x: 63.6, y: 6.3, path: [[35.6, 94.7], [26.2, 94.0], [22.1, 91.8], [20.7, 88.2], [31.6, 82.8], [43.7, 80.3], [59.1, 73.5], [59.1, 72.6], [51.9, 69.0], [51.4, 63.1], [46.0, 59.8], [45.5, 57.0], [60.9, 48.9], [61.3, 45.5], [52.8, 41.5], [52.8, 37.4], [46.4, 34.3], [46.4, 32.7], [55.9, 27.5], [55.9, 25.3], [51.0, 22.8], [51.0, 20.5], [53.7, 19.2], [55.0, 17.1], [59.1, 15.1], [59.1, 8.6], [63.6, 6.3]] },
      { x: 23.4, y: 24.4, path: [[35.6, 94.7], [26.2, 94.0], [22.1, 91.8], [20.7, 88.2], [31.6, 82.8], [43.7, 80.3], [59.1, 73.5], [59.1, 72.6], [51.9, 69.0], [51.4, 63.1], [46.0, 59.8], [45.5, 57.0], [60.9, 48.9], [61.3, 45.5], [52.8, 41.5], [52.8, 38.3], [49.6, 36.8], [39.2, 34.3], [36.1, 34.3], [30.2, 31.3], [27.1, 26.2], [23.4, 24.4]] },
      { x: 79.8, y: 31.1, path: [[35.6, 94.7], [26.2, 94.0], [22.1, 91.8], [20.7, 88.2], [31.6, 82.8], [43.7, 80.3], [59.1, 73.5], [59.1, 72.6], [51.9, 69.0], [51.4, 63.1], [46.0, 59.8], [45.5, 57.0], [60.9, 48.9], [61.3, 46.2], [75.8, 38.8], [75.8, 33.1], [79.8, 31.1]] },
      { x: 28.9, y: 48.0, path: [[35.6, 94.7], [26.2, 94.0], [22.1, 91.8], [20.7, 88.2], [31.6, 82.8], [43.7, 80.3], [49.2, 77.6], [52.3, 76.9], [59.1, 73.5], [59.1, 72.6], [51.9, 69.0], [51.4, 63.1], [46.0, 59.8], [45.5, 56.4], [28.9, 48.0]] },
      { x: 24.4, y: 69.7, path: [[35.6, 94.7], [26.2, 94.0], [22.5, 92.2], [20.7, 89.5], [20.7, 87.0], [26.6, 84.1], [27.5, 83.0], [27.5, 72.2], [26.6, 70.8], [24.4, 69.7]] },
      { x: 61.8, y: 86.1, path: [[35.6, 94.7], [45.1, 95.8], [49.2, 93.8], [53.2, 90.4], [61.8, 86.1]] },
    ],
  },
  {
    id: 'hell-4',
    theme: 'hell',
    image: '/forests/hell-4.webp',
    home: { x: 58.6, y: 93.8 },
    pois: [
      { x: 28.9, y: 11.3, path: [[58.6, 93.8], [51.0, 95.6], [37.9, 95.6], [28.4, 91.8], [25.3, 88.4], [19.8, 85.7], [19.4, 84.6], [19.4, 78.7], [16.7, 76.7], [16.7, 75.1], [30.7, 68.3], [35.6, 67.2], [51.0, 59.1], [51.4, 56.1], [44.6, 52.5], [44.6, 46.2], [43.3, 45.5], [43.3, 43.1], [56.4, 35.6], [56.4, 33.8], [51.4, 31.3], [45.5, 30.0], [38.8, 26.6], [38.8, 25.9], [34.7, 23.4], [34.3, 19.6], [35.2, 19.2], [35.2, 16.7], [31.1, 12.4], [28.9, 11.3]] },
      { x: 71.3, y: 20.7, path: [[58.6, 93.8], [51.0, 95.6], [37.9, 95.6], [33.8, 94.5], [28.4, 91.8], [25.3, 88.4], [19.8, 85.7], [19.4, 78.7], [16.7, 76.7], [16.7, 75.1], [30.7, 68.3], [35.6, 67.2], [51.0, 59.1], [51.4, 56.1], [44.6, 52.5], [44.6, 46.2], [43.3, 45.5], [43.3, 43.1], [56.4, 35.6], [56.4, 32.0], [55.0, 31.1], [55.0, 29.1], [71.3, 20.7]] },
      { x: 28.4, y: 36.1, path: [[58.6, 93.8], [51.0, 95.6], [37.9, 95.6], [28.4, 91.8], [25.3, 88.4], [19.8, 85.7], [19.4, 78.7], [16.7, 76.7], [16.7, 75.1], [30.7, 68.3], [35.6, 67.2], [46.4, 61.8], [51.4, 58.4], [51.4, 56.1], [44.6, 52.5], [44.6, 48.0], [41.9, 46.9], [36.5, 46.4], [31.1, 43.7], [30.2, 42.6], [29.8, 36.8], [28.4, 36.1]] },
      { x: 57.3, y: 48.7, path: [[58.6, 93.8], [51.0, 95.6], [37.9, 95.6], [33.8, 94.5], [28.4, 91.8], [25.3, 88.4], [19.8, 85.7], [19.4, 78.7], [16.7, 76.7], [16.7, 75.1], [30.7, 68.3], [40.6, 65.4], [45.1, 65.4], [48.7, 67.0], [54.6, 67.6], [78.5, 67.2], [82.1, 65.4], [82.1, 64.5], [89.3, 60.9], [90.6, 59.3], [90.6, 57.0], [89.3, 56.4], [59.5, 49.8], [57.3, 48.7]] },
      { x: 28.0, y: 59.8, path: [[58.6, 93.8], [51.0, 95.6], [37.9, 95.6], [28.4, 91.8], [25.3, 88.4], [19.8, 85.7], [19.4, 78.7], [16.7, 76.7], [16.7, 74.4], [24.8, 70.3], [27.1, 68.3], [27.1, 60.2], [28.0, 59.8]] },
      { x: 52.8, y: 79.4, path: [[58.6, 93.8], [51.0, 95.6], [41.9, 95.6], [38.8, 94.0], [38.8, 91.5], [42.8, 89.3], [43.3, 87.3], [46.9, 84.3], [47.4, 82.1], [52.8, 79.4]] },
    ],
  },
]

/**
 * 🎨 v2.51 (per Javi: "use the existing images and keep the same
 * format"): EVERY field painting renders bled — width-driven with a
 * vertical pan — so all four biomes get the zoomed, no-blue frame the
 * extended forests introduced, with no new art required. Throne rooms
 * are deliberately left out: their boss must stay in view without
 * scrolling.
 */
for (const forest of FORESTS) forest.bleed = true

/**
 * 🏟️ Boss arenas (v1.42, per Javi): one painted throne room per biome.
 * The boss enters through the TOP gate; the player's team lines up at the
 * BOTTOM entrance (no basecamp here). Same portrait format as the field
 * maps; no trail POIs — boss fights place everything in board units.
 */
const BOSS_RING_Y: Record<ForestTheme, number> = {
  // 🔥 v6.28: hell's boss used to plant its feet on the sigil's LOWER
  // edge (45), which is exactly where the team now stands — it steps
  // back to the sigil's middle, like the other three.
  forest: 40, desert: 47, tundra: 47, hell: 41,
}

/** 🏟️ v6.28 (per Javi: "make the creatures line up in the inner circle
 * too, on the bottom half"): each portrait room's painted inner ring,
 * measured off the art. In the paintings' 3/4 view a figure standing at
 * the ring's centre has its feet at the ellipse's centre, so the boss
 * spot sits on (or a touch below) `y` and the team's feet on the lower
 * rim, `y + ry`. */
const BOSS_RINGS: Record<ForestTheme, { y: number; rx: number; ry: number; spill?: number }> = {
  forest: { y: 40, rx: 16, ry: 7 },
  desert: { y: 45, rx: 15, ry: 7 },
  tundra: { y: 47, rx: 17, ry: 7 },
  hell: { y: 38.5, rx: 17, ry: 7.5 },
}

export const BOSS_ROOMS: ForestDef[] = FOREST_THEMES.map((theme) => {
  const ring = BOSS_RINGS[theme]
  return {
    id: `${theme}-boss`,
    theme,
    image: `/forests/${theme}-boss.webp`,
    home: { x: 50, y: 90 },
    pois: [],
    bossSpot: { x: 50, y: BOSS_RING_Y[theme] },
    arena: {
      ring: { x: 50, y: ring.y, rx: ring.rx, ry: ring.ry },
      gate: { x: 50, y: 2 },
      teamSide: 'bottom',
      muster: { x: 50, y: ring.y + ring.ry },
      spill: ring.spill,
    },
  }
})

/**
 * 🏟️ v6.28 WIDE boss arenas (Javi's PC/tablet repaints, 1448×1086): the
 * same four throne rooms turned sideways. The team comes in over the
 * LEFT bridge and lines the left rim of the inner circle; the boss walks
 * in over the RIGHT bridge and lands in the middle. Landscape viewports
 * pick these (useEncounter); phones keep the portrait rooms.
 */
export const BOSS_ROOMS_WIDE: ForestDef[] = FOREST_THEMES.map(theme => ({
  id: `${theme}-boss-wide`,
  theme,
  image: `/forests/${theme}-boss-wide.webp`,
  animation: `/forests/${theme}-boss-wide-motion-v2.mp4`,
  home: { x: 8, y: 52 },
  pois: [],
  bossSpot: { x: 50, y: 51 },
  bleed: true,
  canvasAspect: '1448 / 1086',
  topAnchor: 0.3,
  bottomAnchor: 0.7,
  tail: `/forests/${theme}-boss-wide-tail.webp`,
  arena: {
    ring: { x: 50, y: 51, rx: 14, ry: 13 },
    gate: { x: 94, y: 52 },
    teamSide: 'left',
    muster: { x: 36, y: 51 },
    spill: 1.4,
  },
}))

export function isBossRoomId(id: string | undefined | null): boolean {
  return !!id && /-boss(-wide)?$/.test(id)
}

/**
 * 🧩 v6.28 puzzle halls (per Javi: "these are to be used for ALL puzzle
 * maps"): one painted hall in portrait (887×1774) and one in landscape
 * (1672×941), shared by every biome — the tail band and backdrop tint
 * are the only per-theme parts. Missions stand on the pedestals: the
 * first pair on the top row, the next pair below, the last pair in the
 * middle (portrait) / centre column (wide), so 2 puzzles read as a pair
 * and 4 as the corners. Straight trails from the entrance stairs.
 */
const PUZZLE_HOME = { x: 50, y: 93 }
const PUZZLE_WIDE_HOME = { x: 50, y: 88 }
const pedestal = (home: { x: number; y: number }, x: number, y: number, travelLength: number): ForestPoi => ({
  x, y, path: [[home.x, home.y], [x, y]], travelLength,
})
const PUZZLE_POIS: ForestPoi[] = [
  pedestal(PUZZLE_HOME, 26, 27, 30), pedestal(PUZZLE_HOME, 74, 27, 30),
  pedestal(PUZZLE_HOME, 26, 66, 18), pedestal(PUZZLE_HOME, 74, 66, 18),
  pedestal(PUZZLE_HOME, 26, 47, 24), pedestal(PUZZLE_HOME, 74, 47, 24),
]
const PUZZLE_WIDE_POIS: ForestPoi[] = [
  pedestal(PUZZLE_WIDE_HOME, 29, 31, 28), pedestal(PUZZLE_WIDE_HOME, 71, 31, 28),
  pedestal(PUZZLE_WIDE_HOME, 29, 65, 20), pedestal(PUZZLE_WIDE_HOME, 71, 65, 20),
  pedestal(PUZZLE_WIDE_HOME, 50, 31, 28), pedestal(PUZZLE_WIDE_HOME, 50, 65, 20),
]

export const PUZZLE_HALLS: ForestDef[] = FOREST_THEMES.flatMap(theme => [
  {
    id: `puzzle-${theme}`,
    theme,
    image: '/forests/puzzle.webp',
    home: PUZZLE_HOME,
    pois: PUZZLE_POIS,
    bleed: true,
    topAnchor: 0.27,
    bottomAnchor: 0.93,
    tail: `/forests/puzzle-${theme}-tail.webp`,
    noCamp: true,
  },
  {
    id: `puzzle-${theme}-wide`,
    theme,
    image: '/forests/puzzle-wide.webp',
    home: PUZZLE_WIDE_HOME,
    pois: PUZZLE_WIDE_POIS,
    bleed: true,
    canvasAspect: '1672 / 941',
    topAnchor: 0.31,
    bottomAnchor: 0.88,
    tail: `/forests/puzzle-${theme}-wide-tail.webp`,
    noCamp: true,
  },
])

export function isPuzzleHallId(id: string | undefined | null): boolean {
  return !!id && id.startsWith('puzzle-')
}

/** The puzzle hall for a biome, in the shape that fits the screen. */
export function puzzleHallId(theme: ForestTheme, landscape: boolean): string {
  return landscape ? `puzzle-${theme}-wide` : `puzzle-${theme}`
}

/** The boss arena for a biome, in the shape that fits the screen. */
export function bossRoomId(theme: ForestTheme, landscape: boolean): string {
  return landscape ? `${theme}-boss-wide` : `${theme}-boss`
}

/**
 * 🌌 Atmosphere backdrop (v2.55, per Javi — "why do we need the colored
 * backgrounds at all"): the field's OWN painting, pre-blurred and dimmed
 * (public/forests/<id>-bg.webp), fills the page behind the board and the
 * roster. The terrain continues everywhere; no flat colour is on show.
 */
export function forestBackdrop(forest: ForestDef | null | undefined): string | null {
  if (!forest) return null
  return forest.image.split('?')[0]!.replace(/\.webp$/, '-bg.webp')
}

export function forestById(id: string | undefined | null): ForestDef | null {
  if (!id) return null
  return FORESTS.find(f => f.id === id)
    ?? EXTENDED_FORESTS.find(f => f.id === id)
    ?? SQUISH_FORESTS.find(f => f.id === id)
    ?? WIDE_FORESTS.find(f => f.id === id)
    ?? BOSS_ROOMS.find(f => f.id === id)
    ?? BOSS_ROOMS_WIDE.find(f => f.id === id)
    ?? PUZZLE_HALLS.find(f => f.id === id)
    ?? null
}

/** Random map id, optionally confined to the act's theme. */
/** 🌲 EXTENDED FORESTS (v2.47): four repaints whose
 * PLAY LAYOUT matches forest-1..4 — the original composition sits
 * scaled/centered with pure wilderness continuing to every edge.
 * Measured by structural template match (corr 0.84–0.93); each POI,
 * trail point and the camp is transformed through its image's rect so
 * markers land on the painted sites. Since v2.51 they are EXTRA
 * forest-biome variants beside forest-1..4, not a global override. */
interface ExtRect {
  /** The original canvas this repaint extends — POIs come from it. */
  base: string
  /** Where that original sits inside the extended canvas (px), measured
   * by structural template match. */
  x: number
  y: number
  w: number
  h: number
  cw: number
  ch: number
  v: number
}

/**
 * 🗺️ ONE CONTINUOUS MAP (v2.60, per Javi's mock-up): the extended canvas
 * is used WHOLE — no cropping. Its top margin sits under the frozen
 * banner, the play area holds the missions, and its bottom margin runs on
 * behind Your Team, continued past the canvas edge by a seamless tile cut
 * from the painting's own last rows (`<id>-tail.webp`). POIs, trails and
 * home are transformed through the measured rect so markers still land on
 * the painted sites.
 *
 * v2.62 (per Javi, the "new maps" batch): the FULL 724×2172 paintings
 * shipped — v2.60 had measured its rects on canvases already cropped to
 * the play area, which left the banner half on flat page backdrop.
 *
 * v2.63 (per Javi on those screenshots: "you can probably zoom it in a
 * bit, there seems to be some extra space on the left hand side and some
 * towards the top and bottom"): each canvas is re-framed onto its OWN
 * content. The window is derived, not eyeballed — the field's anchors
 * plus the marker footprint measured in the browser (ink reaches 70px
 * above an anchor, 62px below a mission, 37px below the camp, 69px to
 * either side), evaluated at a 360px design viewport. Centring on the
 * content is what kills the left-hand gap: these paintings all sit a
 * little right of centre (e.g. desert-1 had 120px spare left, 32px
 * right). Zoom lands at 1.13× (hell-1, whose sites nearly touch the
 * edges) to 1.79× (forest-4, the tightest composition).
 */
/** Cache-buster for the tail bands alone — see EXTENDED_FORESTS. */
const TAIL_VERSION = 10

const EXT_RECTS: Record<string, ExtRect> = {
  'forest-ext-1': { base: 'forest-1', x: -17, y: 245, w: 585, h: 1171, cw: 532, ch: 1560, v: 9 },
  'forest-ext-2': { base: 'forest-2', x: -22, y: 257, w: 585, h: 1171, cw: 536, ch: 1537, v: 9 },
  'forest-ext-3': { base: 'forest-3', x: -20, y: 231, w: 550, h: 1100, cw: 521, ch: 1413, v: 9 },
  'forest-ext-4': { base: 'forest-4', x: -40, y: 121, w: 514, h: 1029, cw: 405, ch: 1228, v: 9 },
  'desert-1-ext': { base: 'desert-1', x: -72, y: 242, w: 656, h: 1312, cw: 572, ch: 1727, v: 9 },
  'desert-2-ext': { base: 'desert-2', x: -114, y: 176, w: 692, h: 1384, cw: 478, ch: 1666, v: 9 },
  'desert-3-ext': { base: 'desert-3', x: -66, y: 240, w: 638, h: 1278, cw: 537, ch: 1580, v: 9 },
  'desert-4-ext': { base: 'desert-4', x: -27, y: 163, w: 638, h: 1278, cw: 545, ch: 1566, v: 9 },
  'hell-1-ext': { base: 'hell-1', x: 23, y: 282, w: 692, h: 1384, cw: 641, ch: 1813, v: 9 },
  'hell-2-ext': { base: 'hell-2', x: -91, y: 196, w: 674, h: 1348, cw: 517, ch: 1660, v: 9 },
  'hell-3-ext': { base: 'hell-3', x: -29, y: 329, w: 620, h: 1242, cw: 583, ch: 1722, v: 9 },
  'tundra-1-ext': { base: 'tundra-1', x: -40, y: 233, w: 656, h: 1312, cw: 553, ch: 1679, v: 9 },
  'tundra-2-ext': { base: 'tundra-2', x: -68, y: 227, w: 620, h: 1242, cw: 512, ch: 1590, v: 9 },
  'tundra-3-ext': { base: 'tundra-3', x: -37, y: 226, w: 638, h: 1278, cw: 533, ch: 1605, v: 9 },
}

export const EXTENDED_FORESTS: ForestDef[] = Object.entries(EXT_RECTS).map(([id, r]) => {
  const base = FORESTS.find(f => f.id === r.base)!
  const tx = (x: number) => +(((r.x + (x / 100) * r.w) / r.cw) * 100).toFixed(1)
  const ty = (y: number) => +(((r.y + (y / 100) * r.h) / r.ch) * 100).toFixed(1)
  const home = { x: tx(base.home.x), y: ty(base.home.y) }
  const pois = base.pois.map(p => ({
    x: tx(p.x),
    y: ty(p.y),
    path: p.path.map(([px, py]) => [tx(px), ty(py)] as [number, number]),
    travelLength: +pathLength(p.path).toFixed(2),
  }))
  const anchors = [...pois.map(p => p.y), home.y]
  return {
    ...base,
    id,
    image: `/forests/${id}.webp?v=${r.v}`,
    // Versioned apart from the canvas (v2.64): the tails were recut on
    // their own, and the canvases are ~150KB each — no reason to make
    // everyone re-download 2.5MB of unchanged painting for it.
    tail: `/forests/${id}-tail.webp?v=${TAIL_VERSION}`,
    bleed: true,
    canvasAspect: `${r.cw} / ${r.ch}`,
    topAnchor: +(Math.min(...anchors) / 100).toFixed(4),
    bottomAnchor: +(Math.max(...anchors) / 100).toFixed(4),
    home,
    pois,
  }
})

/** 🧪 v4.51 (art by Javi): the SQUISHED-composition test — every POI in
 * one screen-height band so assigning never scrolls. Rolled EXCLUSIVELY
 * (every combat node, every biome) while Javi evaluates the feel; set
 * to null to restore the extended rotation. */
const SQUISH_TEST_ID: string | null = null

export const SQUISH_FORESTS: ForestDef[] = [
  {
    id: 'forest-squish-1',
    theme: 'forest',
    // v4.52 (per Javi: "the width was also squished"): the canvas is now
    // CROPPED to the painting's content band (source 140–594 × 640–1640)
    // so the landmarks span the full screen width. ?v=2 = the in-place
    // recut of both files.
    image: '/forests/forest-squish-1.webp?v=2',
    tail: '/forests/forest-squish-1-tail.webp?v=2',
    bleed: true,
    canvasAspect: '454 / 1000',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.156,
    bottomAnchor: 0.764,
    home: { x: 44.1, y: 76.4 },
    pois: [
      { x: 82.8, y: 15.6, travelLength: 104, path: [[44.1, 76.4], [30.0, 70.0], [33.5, 68.4], [37.9, 68.0], [52.0, 61.6], [60.8, 60.8], [66.1, 58.4], [66.1, 57.6], [62.6, 56.8], [54.6, 53.2], [54.6, 51.2], [62.6, 47.6], [73.1, 46.4], [79.3, 43.6], [79.3, 42.8], [76.7, 42.0], [62.6, 41.6], [50.2, 35.6], [50.2, 34.4], [49.3, 34.0], [49.3, 31.2], [63.4, 24.8], [72.2, 24.0], [80.2, 20.4], [80.2, 18.0], [81.1, 17.6], [81.1, 16.4], [82.8, 15.6]] },
      { x: 30.0, y: 17.6, travelLength: 98, path: [[44.1, 76.4], [30.0, 70.0], [33.5, 68.4], [37.9, 68.0], [52.0, 61.6], [60.8, 60.8], [66.1, 58.4], [66.1, 57.6], [62.6, 56.8], [54.6, 53.2], [54.6, 51.2], [62.6, 47.6], [73.1, 46.4], [79.3, 43.6], [79.3, 42.8], [72.2, 41.6], [62.6, 41.6], [54.6, 38.0], [50.2, 35.6], [50.2, 34.4], [49.3, 34.0], [49.3, 30.4], [50.2, 30.0], [50.2, 28.8], [34.4, 22.4], [32.6, 21.2], [32.6, 18.8], [30.0, 17.6]] },
      { x: 16.7, y: 38.8, travelLength: 66, path: [[44.1, 76.4], [30.0, 70.0], [33.5, 68.4], [37.9, 68.0], [52.0, 61.6], [60.8, 60.8], [66.1, 58.4], [66.1, 57.6], [62.6, 56.8], [54.6, 53.2], [54.6, 50.8], [47.6, 47.6], [30.0, 45.6], [20.3, 41.2], [16.7, 38.8]] },
      { x: 80.2, y: 53.6, travelLength: 40, path: [[44.1, 76.4], [30.0, 70.0], [33.5, 68.4], [37.9, 68.0], [52.0, 61.6], [62.6, 60.8], [80.2, 53.6]] },
      { x: 25.6, y: 58.4, travelLength: 25, path: [[44.1, 76.4], [30.0, 70.0], [30.0, 69.2], [37.9, 65.2], [30.8, 62.0], [29.1, 60.8], [29.1, 60.0], [25.6, 58.4]] },
      { x: 69.6, y: 68.0, travelLength: 16, path: [[44.1, 76.4], [47.6, 74.4], [49.3, 74.4], [54.6, 72.0], [60.8, 72.0], [69.6, 68.0]] },
    ],
  },

  // 🗺️ v5.35 (art by Javi): the one-screen-band family, ALL BIOMES —
  // 11 new paintings. POIs/home are the squish template transformed by
  // a generic content band (PLACEHOLDER — Javi re-traces after visual
  // review; travel lengths inherited from forest-squish-1).
  {
    id: 'forest-comp-2',
    theme: 'forest',
    image: '/forests/forest-comp-2.webp',
    tail: '/forests/forest-comp-2-tail.webp',
    bleed: true,
    canvasAspect: '841 / 1870',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.295,
    bottomAnchor: 0.702,
    home: { x: 62.3, y: 70.2 },
    pois: [
      { x: 69.0, y: 29.5, travelLength: 67, path: [[62.3, 70.2], [49.0, 70.8], [45.2, 69.1], [58.5, 64.8], [63.7, 61.8], [58.0, 60.7], [54.2, 59.0], [53.7, 56.9], [45.2, 53.0], [49.5, 50.9], [50.9, 49.6], [50.9, 48.3], [43.3, 44.9], [43.3, 43.4], [58.5, 35.7], [62.3, 35.1], [67.1, 32.9], [67.5, 30.2], [69.0, 29.5]] },
      { x: 32.3, y: 31.0, travelLength: 70, path: [[62.3, 70.2], [49.0, 70.8], [45.2, 69.1], [58.5, 64.8], [63.7, 62.2], [54.2, 59.0], [53.7, 56.9], [45.2, 53.0], [49.5, 50.9], [50.9, 49.6], [50.9, 48.3], [43.3, 44.9], [43.3, 43.4], [54.7, 38.1], [55.2, 37.2], [52.3, 36.4], [40.9, 35.9], [34.7, 33.2], [32.3, 31.0]] },
      { x: 71.3, y: 46.6, travelLength: 43, path: [[62.3, 70.2], [49.0, 70.8], [45.2, 69.1], [58.5, 64.8], [63.7, 62.2], [54.2, 59.0], [53.7, 57.1], [70.4, 50.1], [71.3, 48.8], [71.3, 46.6]] },
      { x: 27.6, y: 48.6, travelLength: 45, path: [[62.3, 70.2], [49.0, 70.8], [45.2, 69.1], [58.5, 64.8], [63.7, 61.8], [58.0, 60.7], [54.2, 59.0], [53.7, 57.3], [48.5, 55.0], [36.1, 53.3], [27.6, 48.6]] },
      { x: 77.1, y: 58.0, travelLength: 29, path: [[62.3, 70.2], [49.0, 70.8], [45.2, 69.1], [55.2, 65.5], [61.8, 64.4], [77.1, 58.0]] },
      { x: 30.0, y: 63.1, travelLength: 20, path: [[62.3, 70.2], [46.1, 70.8], [33.3, 66.3], [30.0, 63.1]] },
    ],
  },
  {
    id: 'forest-comp-3',
    theme: 'forest',
    image: '/forests/forest-comp-3.webp',
    tail: '/forests/forest-comp-3-tail.webp',
    bleed: true,
    canvasAspect: '841 / 1870',
    topAnchor: 0.329,
    bottomAnchor: 0.738,
    home: { x: 45.2, y: 73.8 },
    pois: [
      { x: 31.4, y: 32.9, travelLength: 60, path: [[45.2, 73.8], [36.6, 70.8], [41.4, 68.7], [41.9, 67.4], [55.6, 62.5], [60.4, 60.3], [60.4, 59.5], [57.6, 58.2], [57.6, 56.9], [50.4, 53.7], [56.6, 50.1], [56.6, 48.8], [52.3, 46.8], [52.3, 44.9], [50.9, 44.3], [50.9, 40.6], [47.6, 39.6], [38.5, 39.6], [33.3, 37.0], [33.3, 33.8], [31.4, 32.9]] },
      { x: 68.5, y: 35.7, travelLength: 56, path: [[45.2, 73.8], [36.6, 70.8], [46.6, 65.2], [60.4, 60.3], [60.4, 59.5], [57.6, 58.2], [57.6, 56.9], [50.4, 53.7], [56.6, 50.1], [56.6, 48.8], [52.3, 46.8], [52.3, 44.9], [50.9, 43.6], [62.8, 42.1], [66.1, 40.6], [66.1, 36.8], [68.5, 35.7]] },
      { x: 28.1, y: 47.5, travelLength: 43, path: [[45.2, 73.8], [36.6, 70.8], [41.4, 68.7], [41.9, 67.4], [55.6, 62.5], [60.4, 60.3], [60.4, 59.5], [52.8, 55.2], [39.5, 53.5], [28.1, 47.5]] },
      { x: 70.4, y: 55.0, travelLength: 29, path: [[45.2, 73.8], [36.6, 70.8], [46.6, 65.2], [57.6, 62.0], [63.7, 59.3], [66.1, 56.9], [70.4, 55.0]] },
      { x: 26.6, y: 66.5, travelLength: 12, path: [[45.2, 73.8], [38.0, 71.7], [36.6, 71.0], [36.6, 70.2], [32.8, 68.4], [26.6, 66.5]] },
      { x: 72.3, y: 72.5, travelLength: 16, path: [[45.2, 73.8], [56.1, 76.8], [64.2, 77.0], [72.3, 72.5]] },
    ],
  },
  {
    id: 'desert-comp-1',
    theme: 'desert',
    image: '/forests/desert-comp-1.webp',
    tail: '/forests/desert-comp-1-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    topAnchor: 0.298,
    bottomAnchor: 0.729,
    home: { x: 38.7, y: 72.9 },
    pois: [
      { x: 73.5, y: 29.8, travelLength: 82, path: [[38.7, 72.9], [44.8, 72.9], [48.1, 71.8], [37.6, 68.3], [37.6, 66.5], [48.6, 62.6], [56.4, 62.2], [63.5, 59.5], [58.0, 57.8], [50.8, 56.9], [43.6, 54.5], [43.6, 53.0], [55.2, 49.5], [58.0, 48.0], [58.0, 47.3], [48.6, 44.2], [48.1, 41.6], [42.5, 39.2], [48.6, 37.6], [56.4, 37.6], [70.2, 33.1], [71.8, 31.7], [71.8, 30.4], [73.5, 29.8]] },
      { x: 37.0, y: 32.6, travelLength: 72, path: [[38.7, 72.9], [44.8, 72.9], [48.1, 71.8], [37.6, 68.3], [37.6, 66.5], [48.6, 62.6], [56.4, 62.2], [63.5, 59.3], [50.8, 56.9], [43.6, 54.5], [43.6, 53.0], [55.2, 49.5], [58.0, 48.0], [58.0, 47.3], [48.1, 43.8], [48.1, 41.6], [42.5, 39.6], [42.5, 38.3], [44.8, 37.6], [44.8, 35.7], [37.0, 32.6]] },
      { x: 70.2, y: 43.4, travelLength: 58, path: [[38.7, 72.9], [44.8, 72.9], [48.1, 71.8], [37.6, 68.3], [37.6, 66.5], [48.6, 62.6], [56.4, 62.2], [63.5, 59.5], [58.0, 57.8], [50.8, 56.9], [43.6, 54.5], [43.6, 53.0], [55.2, 49.5], [58.0, 47.5], [70.2, 43.4]] },
      { x: 28.7, y: 46.0, travelLength: 53, path: [[38.7, 72.9], [44.8, 72.9], [48.1, 71.8], [37.6, 68.3], [37.6, 66.5], [48.6, 62.6], [56.4, 62.2], [63.5, 59.3], [50.8, 56.9], [43.6, 54.5], [44.2, 50.4], [28.7, 46.0]] },
      { x: 71.8, y: 54.3, travelLength: 36, path: [[38.7, 72.9], [44.8, 72.9], [48.1, 71.8], [37.6, 68.3], [37.6, 66.5], [48.6, 62.6], [56.4, 62.2], [63.0, 60.0], [63.5, 57.1], [71.8, 54.3]] },
      { x: 30.4, y: 58.4, travelLength: 28, path: [[38.7, 72.9], [44.8, 72.9], [48.1, 71.8], [37.6, 68.3], [37.6, 66.5], [45.9, 63.1], [40.3, 62.4], [30.4, 58.4]] },
      { x: 64.6, y: 68.8, travelLength: 14, path: [[38.7, 72.9], [53.6, 72.3], [64.6, 68.8]] },
    ],
  },
  {
    id: 'desert-comp-2',
    theme: 'desert',
    image: '/forests/desert-comp-2.webp',
    tail: '/forests/desert-comp-2-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.313,
    bottomAnchor: 0.668,
    home: { x: 24.3, y: 66.8 },
    pois: [
      { x: 74.0, y: 31.3, travelLength: 62, path: [[24.3, 66.8], [36.5, 66.3], [44.8, 63.1], [45.3, 60.9], [43.1, 60.2], [43.1, 58.5], [47.5, 56.9], [47.5, 55.2], [44.8, 54.3], [44.8, 52.5], [55.8, 48.6], [55.8, 46.6], [50.3, 44.4], [50.3, 41.2], [44.2, 39.2], [43.6, 38.3], [60.2, 36.4], [74.0, 31.3]] },
      { x: 33.1, y: 34.1, travelLength: 55, path: [[24.3, 66.8], [36.5, 66.3], [44.8, 63.1], [45.3, 60.9], [43.1, 60.2], [43.1, 58.5], [47.5, 56.9], [47.5, 55.2], [44.8, 54.3], [44.8, 52.5], [55.8, 48.6], [55.8, 46.6], [50.3, 44.4], [50.3, 41.2], [43.6, 38.7], [43.6, 37.7], [47.5, 35.9], [33.1, 34.1]] },
      { x: 72.9, y: 44.7, travelLength: 41, path: [[24.3, 66.8], [36.5, 66.3], [44.8, 63.1], [45.3, 60.9], [43.1, 60.2], [43.1, 58.5], [47.5, 56.9], [47.5, 55.2], [44.8, 54.3], [44.8, 52.6], [63.0, 46.6], [72.9, 44.7]] },
      { x: 25.4, y: 45.1, travelLength: 40, path: [[24.3, 66.8], [36.5, 66.3], [44.8, 63.1], [45.3, 60.9], [43.1, 60.2], [43.1, 58.5], [47.5, 56.9], [47.5, 55.2], [44.8, 54.3], [44.8, 52.5], [47.0, 51.2], [47.0, 49.7], [42.0, 48.4], [33.1, 47.7], [25.4, 45.1]] },
      { x: 24.9, y: 55.0, travelLength: 25, path: [[24.3, 66.8], [36.5, 66.3], [45.3, 62.8], [45.3, 61.8], [31.5, 58.0], [24.9, 55.0]] },
      { x: 68.5, y: 57.1, travelLength: 25, path: [[24.3, 66.8], [38.7, 66.3], [48.6, 63.0], [58.0, 61.8], [65.2, 59.5], [66.3, 58.0], [68.5, 57.1]] },
    ],
  },
  {
    id: 'desert-comp-3',
    theme: 'desert',
    image: '/forests/desert-comp-3.webp',
    tail: '/forests/desert-comp-3-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.285,
    bottomAnchor: 0.67,
    home: { x: 33.7, y: 67.0 },
    pois: [
      { x: 40.3, y: 28.5, travelLength: 66, path: [[33.7, 67.0], [40.9, 65.2], [47.5, 64.6], [35.4, 61.1], [35.4, 59.8], [46.4, 56.3], [48.6, 54.1], [51.9, 52.8], [51.9, 51.7], [42.0, 48.4], [40.3, 46.8], [54.1, 42.3], [54.1, 41.0], [55.2, 40.5], [55.2, 39.8], [43.6, 35.3], [43.6, 34.6], [51.4, 32.2], [51.4, 31.7], [40.3, 28.5]] },
      { x: 72.4, y: 34.6, travelLength: 56, path: [[33.7, 67.0], [40.9, 65.2], [47.5, 64.6], [35.4, 61.1], [35.4, 59.8], [46.4, 56.3], [48.6, 54.1], [51.9, 52.8], [51.9, 51.7], [42.0, 48.4], [40.3, 46.8], [66.3, 38.3], [69.6, 35.5], [72.4, 34.6]] },
      { x: 25.4, y: 41.8, travelLength: 44, path: [[33.7, 67.0], [40.9, 65.2], [47.5, 64.6], [35.4, 61.1], [35.4, 59.8], [46.4, 56.3], [48.6, 54.1], [51.9, 52.8], [51.9, 51.9], [32.0, 45.3], [25.4, 41.8]] },
      { x: 70.2, y: 46.8, travelLength: 39, path: [[33.7, 67.0], [40.9, 65.2], [47.5, 64.6], [35.4, 61.1], [35.4, 59.8], [54.7, 53.0], [62.4, 52.5], [67.4, 50.8], [69.1, 49.3], [69.1, 47.1], [70.2, 46.8]] },
      { x: 27.1, y: 53.8, travelLength: 25, path: [[33.7, 67.0], [47.5, 64.4], [35.4, 61.1], [35.4, 58.9], [38.1, 57.8], [27.1, 53.8]] },
      { x: 71.3, y: 61.7, travelLength: 20, path: [[33.7, 67.0], [37.0, 67.0], [40.9, 65.7], [60.2, 65.7], [71.3, 61.7]] },
    ],
  },
  {
    id: 'tundra-comp-1',
    theme: 'tundra',
    image: '/forests/tundra-comp-1.webp',
    tail: '/forests/tundra-comp-1-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.302,
    bottomAnchor: 0.687,
    home: { x: 39.8, y: 68.7 },
    pois: [
      { x: 35.9, y: 30.2, travelLength: 60, path: [[39.8, 68.7], [40.3, 67.6], [39.8, 67.4], [39.8, 63.0], [51.4, 59.1], [52.5, 58.5], [52.5, 56.5], [58.6, 54.3], [45.3, 50.4], [45.3, 49.7], [56.9, 46.0], [56.9, 44.5], [54.1, 43.6], [54.1, 42.7], [61.3, 40.3], [61.3, 39.6], [53.0, 38.5], [45.3, 35.9], [43.6, 34.1], [35.9, 30.2]] },
      { x: 71.8, y: 34.1, travelLength: 52, path: [[39.8, 68.7], [40.3, 67.6], [39.8, 67.4], [39.8, 63.0], [51.4, 59.1], [52.5, 58.5], [52.5, 56.5], [58.6, 54.3], [45.3, 50.4], [45.3, 49.7], [56.9, 46.0], [56.9, 44.5], [54.1, 43.6], [54.1, 42.9], [69.6, 38.1], [70.2, 34.6], [71.8, 34.1]] },
      { x: 33.1, y: 44.9, travelLength: 40, path: [[39.8, 68.7], [40.3, 67.6], [39.8, 67.4], [39.8, 63.0], [51.4, 59.1], [52.5, 58.5], [52.5, 56.5], [58.6, 54.1], [45.3, 50.4], [45.3, 48.8], [49.2, 47.3], [38.1, 46.4], [33.1, 44.9]] },
      { x: 70.7, y: 50.4, travelLength: 27, path: [[39.8, 68.7], [40.3, 67.6], [39.8, 67.4], [39.8, 63.0], [51.4, 59.1], [52.5, 58.5], [52.5, 56.5], [58.6, 53.9], [70.7, 50.4]] },
      { x: 31.5, y: 56.3, travelLength: 15, path: [[39.8, 68.7], [40.3, 67.6], [39.8, 67.4], [39.8, 63.0], [42.0, 61.3], [35.4, 59.1], [33.7, 57.1], [31.5, 56.3]] },
      { x: 69.1, y: 63.9, travelLength: 18, path: [[39.8, 68.7], [62.4, 68.7], [67.4, 66.8], [67.4, 64.4], [69.1, 63.9]] },
    ],
  },
  {
    id: 'tundra-comp-2',
    theme: 'tundra',
    image: '/forests/tundra-comp-2.webp',
    tail: '/forests/tundra-comp-2-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.302,
    bottomAnchor: 0.687,
    home: { x: 32.6, y: 68.7 },
    pois: [
      { x: 32.0, y: 30.2, travelLength: 63, path: [[32.6, 68.7], [40.3, 66.8], [53.6, 62.0], [53.6, 61.3], [43.1, 57.8], [43.1, 54.9], [55.8, 51.2], [57.5, 49.9], [49.2, 47.1], [48.6, 45.1], [42.0, 42.9], [42.0, 40.9], [49.7, 38.3], [51.4, 37.4], [51.4, 36.1], [43.6, 35.0], [32.0, 30.2]] },
      { x: 72.4, y: 30.9, travelLength: 62, path: [[32.6, 68.7], [40.3, 66.8], [53.6, 62.0], [53.6, 61.3], [43.1, 57.8], [43.1, 54.9], [55.8, 51.2], [57.5, 49.9], [49.2, 47.1], [48.6, 45.1], [42.0, 42.9], [42.0, 41.6], [60.8, 36.4], [63.0, 34.8], [72.4, 30.9]] },
      { x: 74.0, y: 48.0, travelLength: 40, path: [[32.6, 68.7], [40.3, 66.8], [53.6, 62.0], [53.6, 61.3], [43.1, 57.8], [43.1, 54.9], [47.0, 53.6], [51.4, 53.2], [64.6, 49.1], [74.0, 48.0]] },
      { x: 28.2, y: 48.4, travelLength: 33, path: [[32.6, 68.7], [51.4, 63.1], [53.6, 62.0], [53.6, 61.3], [43.1, 57.8], [43.1, 53.6], [28.2, 48.4]] },
      { x: 74.6, y: 58.2, travelLength: 24, path: [[32.6, 68.7], [55.8, 62.6], [63.0, 62.0], [74.6, 58.2]] },
      { x: 27.6, y: 59.8, travelLength: 15, path: [[32.6, 68.7], [41.4, 66.3], [42.0, 65.3], [33.1, 62.8], [27.6, 59.8]] },
    ],
  },
  {
    id: 'tundra-comp-3',
    theme: 'tundra',
    image: '/forests/tundra-comp-3.webp',
    tail: '/forests/tundra-comp-3-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.271,
    bottomAnchor: 0.683,
    home: { x: 37.6, y: 68.3 },
    pois: [
      { x: 33.1, y: 27.1, travelLength: 68, path: [[37.6, 68.3], [38.1, 64.6], [47.0, 61.7], [51.4, 61.5], [61.3, 58.2], [61.3, 57.2], [55.2, 56.1], [45.9, 53.0], [43.6, 51.4], [34.3, 48.2], [34.3, 47.1], [52.5, 41.6], [52.5, 40.1], [42.5, 36.8], [42.5, 34.8], [47.0, 33.3], [47.5, 32.4], [33.1, 27.1]] },
      { x: 71.3, y: 35.5, travelLength: 58, path: [[37.6, 68.3], [38.1, 64.6], [47.0, 61.7], [51.4, 61.5], [61.3, 58.2], [61.3, 57.2], [55.2, 56.1], [44.8, 52.6], [43.6, 51.4], [34.3, 48.2], [34.3, 47.1], [43.6, 44.2], [53.6, 42.5], [63.5, 39.2], [66.9, 37.0], [71.3, 35.5]] },
      { x: 24.3, y: 40.3, travelLength: 49, path: [[37.6, 68.3], [38.1, 64.6], [47.0, 61.7], [51.4, 61.5], [61.3, 58.2], [61.3, 57.2], [55.2, 56.1], [45.9, 53.0], [43.6, 51.4], [34.3, 48.2], [34.3, 46.9], [40.3, 44.9], [40.3, 44.0], [32.6, 43.1], [24.3, 40.3]] },
      { x: 71.3, y: 52.1, travelLength: 28, path: [[37.6, 68.3], [38.1, 64.6], [47.0, 61.7], [51.4, 61.5], [61.3, 58.2], [61.3, 56.3], [59.1, 55.2], [63.5, 54.7], [71.3, 52.1]] },
      { x: 29.3, y: 56.5, travelLength: 17, path: [[37.6, 68.3], [38.1, 64.1], [43.6, 61.8], [29.3, 56.5]] },
      { x: 68.5, y: 65.7, travelLength: 17, path: [[37.6, 68.3], [60.8, 69.0], [68.5, 65.7]] },
    ],
  },
  {
    id: 'hell-comp-1',
    theme: 'hell',
    image: '/forests/hell-comp-1.webp',
    tail: '/forests/hell-comp-1-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.289,
    bottomAnchor: 0.653,
    home: { x: 27.6, y: 65.3 },
    pois: [
      { x: 36.5, y: 28.9, travelLength: 66, path: [[27.6, 65.3], [37.0, 64.8], [50.3, 60.9], [54.1, 58.9], [54.7, 57.4], [47.0, 54.9], [47.0, 53.4], [52.5, 51.5], [59.1, 51.0], [64.1, 49.3], [64.1, 48.6], [51.9, 45.5], [51.9, 44.4], [42.0, 41.0], [41.4, 40.3], [42.5, 39.9], [45.9, 39.8], [50.8, 38.1], [50.8, 36.4], [47.5, 35.3], [47.5, 32.9], [36.5, 28.9]] },
      { x: 76.8, y: 29.5, travelLength: 71, path: [[27.6, 65.3], [37.0, 64.8], [50.3, 60.9], [54.1, 58.9], [54.7, 57.4], [47.0, 54.9], [47.0, 53.4], [52.5, 51.5], [59.1, 51.0], [64.1, 49.3], [64.1, 48.6], [63.0, 48.2], [51.9, 45.5], [51.9, 44.4], [41.4, 40.3], [45.9, 39.8], [55.2, 36.1], [69.6, 35.0], [75.1, 32.9], [75.7, 29.8], [76.8, 29.5]] },
      { x: 71.8, y: 43.8, travelLength: 40, path: [[27.6, 65.3], [37.0, 64.8], [53.0, 59.6], [54.7, 58.4], [54.7, 57.4], [47.0, 54.9], [47.0, 53.4], [52.5, 51.5], [59.1, 51.0], [64.1, 49.3], [64.6, 46.2], [71.8, 43.8]] },
      { x: 24.9, y: 45.1, travelLength: 38, path: [[27.6, 65.3], [33.7, 65.3], [46.4, 62.2], [53.0, 59.6], [54.7, 58.4], [54.7, 57.4], [47.0, 54.9], [47.0, 51.2], [36.5, 49.7], [24.9, 45.1]] },
      { x: 29.3, y: 56.9, travelLength: 16, path: [[27.6, 65.3], [33.7, 65.3], [41.4, 63.3], [34.3, 60.6], [33.7, 59.1], [29.3, 56.9]] },
      { x: 76.2, y: 58.2, travelLength: 31, path: [[27.6, 65.3], [37.0, 64.8], [44.2, 62.4], [48.6, 62.2], [54.7, 60.2], [76.2, 61.7], [79.0, 60.6], [79.0, 59.1], [76.2, 58.2]] },
    ],
  },
  {
    id: 'hell-comp-2',
    theme: 'hell',
    image: '/forests/hell-comp-2.webp',
    tail: '/forests/hell-comp-2-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.265,
    bottomAnchor: 0.604,
    home: { x: 36.5, y: 60.4 },
    pois: [
      { x: 49.7, y: 26.5, travelLength: 52, path: [[36.5, 60.4], [36.5, 56.3], [56.4, 51.0], [56.4, 49.5], [52.5, 48.2], [52.5, 47.1], [61.3, 44.2], [61.3, 42.9], [53.6, 39.8], [53.6, 38.3], [45.3, 35.5], [45.3, 34.4], [54.7, 32.2], [54.7, 30.7], [51.4, 29.6], [50.8, 26.9], [49.7, 26.5]] },
      { x: 21.0, y: 35.7, travelLength: 44, path: [[36.5, 60.4], [36.5, 56.3], [43.6, 53.9], [49.7, 53.2], [56.4, 51.0], [56.4, 50.3], [42.5, 47.7], [35.9, 45.5], [32.0, 43.3], [32.0, 41.8], [36.5, 39.8], [21.0, 35.7]] },
      { x: 75.7, y: 36.8, travelLength: 37, path: [[36.5, 60.4], [36.5, 56.3], [56.4, 51.0], [56.4, 49.5], [52.5, 48.2], [52.5, 47.1], [61.3, 44.2], [61.3, 42.5], [70.2, 39.6], [75.7, 36.8]] },
      { x: 70.2, y: 48.4, travelLength: 23, path: [[36.5, 60.4], [36.5, 56.3], [45.3, 53.6], [62.4, 51.5], [70.2, 48.4]] },
      { x: 24.3, y: 50.3, travelLength: 15, path: [[36.5, 60.4], [36.5, 56.0], [38.7, 54.7], [24.3, 50.3]] },
      { x: 75.7, y: 59.3, travelLength: 21, path: [[36.5, 60.4], [69.6, 61.8], [75.7, 59.3]] },
    ],
  },
  {
    id: 'hell-comp-3',
    theme: 'hell',
    image: '/forests/hell-comp-3.webp',
    tail: '/forests/hell-comp-3-tail.webp',
    bleed: true,
    canvasAspect: '724 / 2173',
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    // v5.79: below-camp site cut (1) — the frame ends at the basecamp.
    topAnchor: 0.283,
    bottomAnchor: 0.701,
    home: { x: 32.6, y: 70.1 },
    pois: [
      { x: 36.5, y: 28.3, travelLength: 68, path: [[32.6, 70.1], [32.6, 68.1], [34.3, 67.2], [50.8, 61.5], [50.8, 59.8], [48.6, 59.1], [48.1, 58.0], [54.7, 55.6], [37.6, 51.2], [37.6, 50.4], [38.7, 50.1], [43.6, 49.5], [53.6, 46.2], [53.6, 44.2], [50.3, 43.1], [50.3, 41.2], [52.5, 40.5], [44.8, 39.0], [39.2, 37.0], [39.2, 35.7], [44.8, 33.9], [44.8, 32.8], [36.5, 28.3]] },
      { x: 72.4, y: 32.9, travelLength: 61, path: [[32.6, 70.1], [32.6, 68.1], [34.3, 67.2], [50.8, 61.5], [50.8, 59.8], [48.6, 59.1], [48.1, 58.0], [54.7, 55.6], [43.1, 53.0], [37.6, 51.2], [37.6, 50.4], [43.6, 49.5], [53.6, 46.2], [53.6, 44.2], [50.3, 43.1], [50.3, 42.2], [65.2, 38.1], [69.1, 35.5], [70.2, 33.7], [72.4, 32.9]] },
      { x: 26.5, y: 45.8, travelLength: 39, path: [[32.6, 70.1], [32.6, 68.1], [34.3, 67.2], [50.8, 61.5], [50.8, 59.8], [48.6, 59.1], [48.1, 58.0], [54.7, 55.6], [37.6, 51.2], [37.6, 49.9], [26.5, 45.8]] },
      { x: 73.5, y: 51.9, travelLength: 31, path: [[32.6, 70.1], [32.6, 68.1], [34.3, 67.2], [50.8, 61.5], [50.8, 59.8], [48.1, 58.4], [56.4, 56.3], [62.4, 56.0], [73.5, 51.9]] },
      { x: 26.0, y: 59.5, travelLength: 12, path: [[32.6, 70.1], [33.1, 64.4], [30.9, 62.2], [26.0, 59.5]] },
      { x: 63.0, y: 66.8, travelLength: 17, path: [[32.6, 70.1], [53.0, 70.7], [63.0, 66.8]] },
    ],
  },
]

/** 🖥️ v5.35 (art by Javi): LANDSCAPE field maps for tablet/PC play —
 * fresh wide compositions (1672×941), one template family. Rolled
 * instead of the portrait set when the viewport is landscape at node
 * entry (no mid-node swapping). POIs/home are PLACEHOLDER estimates
 * from forestpc1 pending Javi's trail traces. */
export const WIDE_FORESTS: ForestDef[] = [
  {
    id: 'forest-wide-1',
    theme: 'forest',
    image: '/forests/forest-wide-1.webp',
    tail: '/forests/forest-wide-1-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.247,
    bottomAnchor: 0.731,
    home: { x: 34.9, y: 50.2 },
    pois: [
      { x: 54.1, y: 24.7, travelLength: 31, path: [[34.9, 50.2], [33.7, 44.6], [35.2, 42.5], [42.3, 40.8], [48.3, 35.3], [51.9, 35.3], [55.3, 29.3], [55.3, 26.8], [54.1, 24.7]] },
      { x: 20.3, y: 26.8, travelLength: 27, path: [[34.9, 50.2], [31.1, 44.2], [28.7, 43.4], [24.9, 43.4], [21.8, 37.4], [21.3, 28.5], [20.3, 26.8]] },
      { x: 81.6, y: 29.3, travelLength: 64, path: [[34.9, 50.2], [33.7, 44.6], [35.2, 42.5], [42.3, 40.8], [48.3, 35.3], [54.1, 35.3], [56.5, 39.5], [61.5, 41.2], [63.6, 45.1], [66.7, 47.2], [68.9, 50.2], [71.3, 51.4], [73.7, 51.4], [74.9, 49.3], [74.9, 45.9], [79.4, 37.4], [81.6, 29.3]] },
      { x: 49.5, y: 47.6, travelLength: 31, path: [[34.9, 50.2], [35.9, 60.8], [39.7, 62.9], [44.7, 62.9], [47.8, 57.4], [49.5, 47.6]] },
      { x: 17.0, y: 69.7, travelLength: 48, path: [[34.9, 50.2], [34.4, 59.9], [31.3, 66.3], [31.3, 75.7], [27.8, 82.0], [23.9, 82.9], [17.0, 69.7]] },
      { x: 81.3, y: 73.1, travelLength: 89, path: [[34.9, 50.2], [33.7, 44.6], [35.2, 42.5], [42.3, 40.8], [48.3, 35.3], [54.1, 35.3], [56.9, 40.0], [61.5, 41.2], [64.8, 45.9], [67.2, 51.0], [67.2, 54.0], [65.3, 58.7], [65.3, 65.9], [61.0, 68.9], [58.4, 73.5], [58.6, 76.9], [63.9, 78.6], [69.9, 83.7], [75.6, 84.2], [81.3, 73.1]] },
    ],
  },
  {
    id: 'forest-wide-2',
    theme: 'forest',
    image: '/forests/forest-wide-2.webp',
    tail: '/forests/forest-wide-2-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.319,
    bottomAnchor: 0.727,
    home: { x: 21.3, y: 65.9 },
    pois: [
      { x: 36.1, y: 31.9, travelLength: 37, path: [[21.3, 65.9], [25.6, 56.1], [25.8, 50.6], [27.3, 48.0], [28.7, 46.8], [34.2, 45.9], [36.6, 41.7], [36.8, 33.2], [36.1, 31.9]] },
      { x: 74.9, y: 41.7, travelLength: 63, path: [[21.3, 65.9], [25.6, 56.1], [25.8, 50.6], [28.7, 46.8], [33.3, 46.3], [35.9, 44.6], [38.8, 44.2], [42.6, 48.9], [46.2, 49.7], [49.8, 53.6], [52.4, 54.0], [55.5, 57.0], [61.7, 57.0], [66.7, 48.9], [71.5, 48.5], [74.9, 41.7]] },
      { x: 56.0, y: 43.4, travelLength: 49, path: [[21.3, 65.9], [25.6, 56.1], [25.8, 50.6], [28.7, 46.8], [33.3, 46.3], [35.9, 44.6], [38.8, 44.2], [42.6, 48.9], [45.5, 49.3], [49.8, 53.6], [51.9, 54.0], [56.0, 43.4]] },
      { x: 63.4, y: 70.1, travelLength: 57, path: [[21.3, 65.9], [22.0, 68.9], [24.9, 71.8], [24.9, 77.4], [26.3, 79.9], [29.4, 81.6], [40.7, 81.6], [43.3, 79.1], [47.4, 72.7], [50.2, 70.1], [52.4, 70.1], [53.1, 74.4], [55.5, 77.8], [60.0, 79.5], [62.2, 79.5], [62.7, 71.4], [63.4, 70.1]] },
      { x: 33.0, y: 70.6, travelLength: 30, path: [[21.3, 65.9], [22.0, 68.9], [24.9, 71.8], [24.9, 77.4], [28.0, 81.2], [34.0, 81.6], [34.7, 80.3], [34.7, 74.0], [33.0, 70.6]] },
      { x: 87.1, y: 72.7, travelLength: 66, path: [[21.3, 65.9], [22.0, 68.9], [24.9, 71.8], [24.9, 77.4], [26.3, 79.9], [29.4, 81.6], [40.7, 81.6], [43.3, 79.1], [47.4, 72.7], [50.2, 70.1], [52.4, 70.1], [53.1, 74.4], [55.5, 77.8], [60.0, 79.5], [64.1, 79.9], [71.3, 79.9], [72.5, 79.1], [82.8, 79.9], [87.1, 72.7]] },
    ],
  },
  {
    id: 'forest-wide-3',
    theme: 'forest',
    image: '/forests/forest-wide-3.webp',
    tail: '/forests/forest-wide-3-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.332,
    bottomAnchor: 0.667,
    home: { x: 22.2, y: 59.1 },
    pois: [
      { x: 39.2, y: 33.2, travelLength: 37, path: [[22.2, 59.1], [19.6, 59.1], [17.9, 55.7], [19.6, 53.6], [21.5, 53.1], [25.8, 45.5], [28.7, 44.2], [32.1, 44.2], [33.7, 45.9], [38.0, 46.3], [38.8, 44.6], [39.2, 33.2]] },
      { x: 13.9, y: 39.1, travelLength: 22, path: [[22.2, 59.1], [19.6, 59.1], [18.2, 56.5], [17.9, 52.3], [13.9, 39.1]] },
      { x: 58.9, y: 40.4, travelLength: 44, path: [[22.2, 59.1], [19.6, 59.1], [17.9, 55.7], [19.6, 53.6], [21.5, 53.1], [25.8, 45.5], [27.3, 44.6], [32.1, 44.2], [33.7, 45.9], [36.1, 46.3], [45.0, 46.3], [51.4, 50.2], [54.8, 50.2], [58.9, 40.4]] },
      { x: 89.0, y: 40.4, travelLength: 66, path: [[22.2, 59.1], [19.6, 59.1], [17.9, 55.7], [19.6, 53.6], [21.5, 53.1], [25.8, 45.5], [28.7, 44.2], [32.1, 44.2], [33.7, 45.9], [36.1, 46.3], [45.0, 46.3], [51.4, 50.2], [56.0, 50.2], [62.0, 54.0], [69.9, 54.8], [75.6, 47.6], [79.9, 47.2], [82.1, 49.3], [85.2, 49.3], [89.0, 40.4]] },
      { x: 58.9, y: 65.5, travelLength: 80, path: [[22.2, 59.1], [19.6, 59.1], [17.9, 55.7], [19.6, 53.6], [21.5, 53.1], [25.8, 45.5], [27.3, 44.6], [32.1, 44.2], [33.7, 45.9], [36.1, 46.3], [45.0, 46.3], [51.4, 50.2], [56.0, 50.2], [62.0, 54.0], [64.6, 54.4], [67.0, 58.7], [69.4, 70.6], [66.5, 74.8], [63.6, 76.9], [56.9, 77.4], [56.5, 70.1], [58.9, 65.5]] },
      { x: 32.1, y: 66.7, travelLength: 10, path: [[22.2, 59.1], [23.2, 59.5], [25.4, 63.3], [27.0, 63.8], [28.7, 65.5], [31.3, 65.5], [32.1, 66.7]] },
    ],
  },
  {
    id: 'desert-wide-1',
    theme: 'desert',
    image: '/forests/desert-wide-1.webp',
    tail: '/forests/desert-wide-1-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.276,
    bottomAnchor: 0.697,
    home: { x: 14.4, y: 63.8 },
    pois: [
      { x: 31.6, y: 27.6, travelLength: 39, path: [[14.4, 63.8], [15.8, 63.8], [17.5, 60.8], [18.2, 57.0], [25.6, 42.9], [29.9, 40.0], [32.8, 34.9], [33.0, 30.2], [31.6, 27.6]] },
      { x: 55.3, y: 29.8, travelLength: 65, path: [[14.4, 63.8], [15.8, 63.8], [17.5, 60.8], [18.2, 57.0], [25.6, 42.9], [32.3, 37.8], [35.2, 37.8], [37.6, 42.1], [41.9, 44.6], [44.5, 49.3], [49.8, 49.7], [54.3, 39.5], [55.3, 29.8]] },
      { x: 83.3, y: 35.7, travelLength: 77, path: [[14.4, 63.8], [15.8, 63.8], [17.5, 60.8], [18.2, 57.0], [25.6, 42.9], [32.3, 37.8], [35.2, 37.8], [37.6, 42.1], [41.9, 44.6], [45.2, 49.7], [51.0, 49.7], [52.6, 46.8], [61.0, 46.8], [62.0, 47.6], [64.8, 47.6], [67.0, 49.7], [73.7, 49.7], [75.1, 48.5], [78.7, 48.5], [80.9, 44.6], [83.3, 35.7]] },
      { x: 7.9, y: 42.9, travelLength: 23, path: [[14.4, 63.8], [15.8, 63.8], [17.5, 60.8], [18.4, 56.1], [17.9, 55.3], [15.8, 54.8], [12.9, 51.9], [7.9, 42.9]] },
      { x: 61.7, y: 62.9, travelLength: 76, path: [[14.4, 63.8], [15.8, 63.8], [17.5, 60.8], [18.2, 57.0], [25.6, 42.9], [32.3, 37.8], [35.2, 37.8], [37.6, 42.1], [41.9, 44.6], [44.5, 49.3], [45.7, 49.7], [50.7, 59.5], [50.7, 68.9], [52.4, 71.0], [58.6, 71.0], [60.5, 67.6], [61.7, 62.9]] },
      { x: 36.1, y: 65.0, travelLength: 30, path: [[14.4, 63.8], [18.7, 63.3], [19.6, 65.5], [19.6, 70.6], [22.0, 74.8], [23.9, 76.1], [29.2, 75.7], [30.6, 73.5], [33.7, 71.4], [35.4, 68.4], [36.1, 65.0]] },
      { x: 84.2, y: 69.7, travelLength: 103, path: [[14.4, 63.8], [15.8, 63.8], [17.5, 60.8], [18.2, 57.0], [25.6, 42.9], [32.3, 37.8], [35.2, 37.8], [37.6, 42.1], [41.9, 44.6], [45.2, 49.7], [51.0, 49.7], [52.6, 46.8], [61.0, 46.8], [62.0, 47.6], [64.8, 47.6], [66.7, 49.3], [73.7, 62.9], [73.9, 67.6], [69.9, 75.7], [69.9, 77.4], [71.1, 79.5], [77.8, 80.3], [84.2, 69.7]] },
    ],
  },
  {
    id: 'desert-wide-2',
    theme: 'desert',
    image: '/forests/desert-wide-2.webp',
    tail: '/forests/desert-wide-2-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.289,
    bottomAnchor: 0.693,
    home: { x: 17.7, y: 45.9 },
    pois: [
      { x: 50.7, y: 28.9, travelLength: 37, path: [[17.7, 45.9], [21.1, 45.9], [21.5, 46.8], [28.2, 46.8], [32.1, 42.9], [38.5, 42.9], [41.6, 46.3], [45.2, 46.3], [46.9, 43.4], [47.6, 35.7], [50.7, 28.9]] },
      { x: 21.1, y: 31.5, travelLength: 16, path: [[17.7, 45.9], [19.9, 45.9], [22.7, 39.5], [22.7, 34.4], [21.1, 31.5]] },
      { x: 76.8, y: 31.9, travelLength: 60, path: [[17.7, 45.9], [21.1, 45.9], [21.5, 46.8], [28.2, 46.8], [32.1, 42.9], [38.5, 42.9], [41.6, 46.3], [45.0, 46.3], [47.4, 50.6], [52.2, 53.1], [56.2, 53.1], [58.9, 49.7], [61.0, 48.5], [66.0, 48.5], [69.1, 50.6], [73.2, 50.6], [74.4, 48.5], [74.4, 38.7], [76.8, 31.9]] },
      { x: 21.3, y: 63.8, travelLength: 66, path: [[17.7, 45.9], [21.1, 45.9], [21.5, 46.8], [28.2, 46.8], [32.1, 42.9], [37.6, 42.9], [40.0, 47.2], [40.0, 49.3], [37.3, 51.9], [33.5, 59.1], [33.3, 66.3], [34.0, 71.4], [30.1, 78.2], [26.8, 78.6], [24.9, 75.2], [21.3, 63.8]] },
      { x: 45.9, y: 66.3, travelLength: 46, path: [[17.7, 45.9], [21.1, 45.9], [21.5, 46.8], [28.2, 46.8], [32.1, 42.9], [37.6, 42.9], [40.0, 47.2], [40.0, 49.3], [37.3, 51.9], [34.2, 57.4], [33.3, 60.4], [33.3, 64.2], [36.4, 68.4], [42.6, 68.4], [45.2, 67.6], [45.9, 66.3]] },
      { x: 80.1, y: 69.3, travelLength: 65, path: [[17.7, 45.9], [21.1, 45.9], [21.5, 46.8], [28.2, 46.8], [32.1, 42.9], [38.5, 42.9], [41.6, 46.3], [45.0, 46.3], [48.8, 51.9], [52.2, 53.1], [55.7, 53.1], [58.1, 55.3], [61.5, 61.2], [61.7, 66.7], [63.6, 70.1], [67.9, 75.2], [72.2, 77.4], [75.6, 77.4], [80.1, 69.3]] },
    ],
  },
  {
    id: 'desert-wide-3',
    theme: 'desert',
    image: '/forests/desert-wide-3.webp',
    tail: '/forests/desert-wide-3-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.183,
    bottomAnchor: 0.667,
    home: { x: 13.2, y: 45.1 },
    pois: [
      { x: 78.5, y: 18.3, travelLength: 61, path: [[13.2, 45.1], [15.1, 45.1], [16.5, 42.5], [19.9, 42.5], [21.5, 39.5], [28.7, 39.5], [33.5, 35.7], [37.8, 35.7], [40.2, 38.7], [45.9, 41.2], [51.0, 40.8], [53.1, 37.0], [62.7, 37.0], [65.8, 31.5], [67.9, 30.6], [74.9, 31.0], [77.0, 26.8], [78.5, 18.3]] },
      { x: 46.9, y: 23.0, travelLength: 46, path: [[13.2, 45.1], [15.1, 45.1], [16.5, 42.5], [19.9, 42.5], [21.5, 39.5], [28.7, 39.5], [33.5, 35.7], [37.8, 35.7], [40.2, 38.7], [44.3, 40.8], [49.5, 41.2], [51.7, 37.4], [51.7, 31.5], [46.9, 23.0]] },
      { x: 19.1, y: 27.2, travelLength: 20, path: [[13.2, 45.1], [15.1, 45.1], [16.5, 42.5], [18.7, 42.5], [19.9, 40.4], [20.8, 30.2], [19.1, 27.2]] },
      { x: 85.6, y: 47.6, travelLength: 68, path: [[13.2, 45.1], [15.1, 45.1], [16.5, 42.5], [19.9, 42.5], [21.5, 39.5], [28.7, 39.5], [33.5, 35.7], [37.8, 35.7], [40.2, 38.7], [44.3, 40.8], [49.5, 41.2], [54.8, 47.2], [64.1, 47.2], [65.8, 48.5], [68.2, 48.5], [73.0, 57.0], [76.3, 59.5], [80.4, 59.5], [85.6, 47.6]] },
      { x: 40.4, y: 59.9, travelLength: 57, path: [[13.2, 45.1], [18.7, 44.2], [19.9, 46.3], [21.8, 47.6], [24.2, 51.9], [23.4, 61.6], [28.9, 71.8], [30.9, 72.7], [35.4, 77.4], [38.3, 77.4], [40.2, 74.0], [40.9, 68.9], [40.4, 59.9]] },
      { x: 15.8, y: 66.7, travelLength: 50, path: [[13.2, 45.1], [18.7, 44.2], [19.9, 46.3], [21.8, 47.6], [24.2, 51.9], [23.4, 61.6], [27.5, 69.3], [27.5, 72.3], [25.6, 75.7], [23.0, 77.4], [19.6, 77.4], [17.2, 72.3], [15.8, 66.7]] },
    ],
  },
  {
    id: 'tundra-wide-1',
    theme: 'tundra',
    image: '/forests/tundra-wide-1.webp',
    tail: '/forests/tundra-wide-1-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.327,
    bottomAnchor: 0.676,
    home: { x: 19.6, y: 58.7 },
    pois: [
      { x: 39.7, y: 32.7, travelLength: 33, path: [[19.6, 58.7], [20.3, 55.3], [21.5, 53.6], [23.9, 53.1], [28.2, 45.9], [29.9, 45.1], [38.8, 45.9], [40.0, 43.4], [39.7, 32.7]] },
      { x: 14.6, y: 38.7, travelLength: 20, path: [[19.6, 58.7], [19.6, 56.1], [17.9, 53.1], [17.9, 50.2], [14.6, 38.7]] },
      { x: 62.0, y: 42.5, travelLength: 39, path: [[19.6, 58.7], [19.6, 56.5], [21.5, 53.6], [23.9, 53.1], [27.8, 46.3], [29.9, 45.1], [43.8, 45.9], [48.6, 49.7], [57.7, 50.2], [62.0, 42.5]] },
      { x: 90.0, y: 44.2, travelLength: 58, path: [[19.6, 58.7], [19.6, 56.5], [21.5, 53.6], [23.9, 53.1], [27.8, 46.3], [29.9, 45.1], [43.8, 45.9], [48.6, 49.7], [58.1, 50.2], [62.7, 53.6], [70.1, 54.0], [74.2, 49.7], [77.0, 48.0], [81.1, 48.0], [83.7, 49.7], [86.8, 49.7], [90.0, 44.2]] },
      { x: 31.3, y: 66.7, travelLength: 34, path: [[19.6, 58.7], [21.5, 58.7], [23.7, 62.9], [20.8, 69.7], [20.8, 74.0], [22.5, 76.5], [30.1, 77.8], [31.1, 75.7], [31.3, 66.7]] },
      { x: 58.6, y: 67.6, travelLength: 46, path: [[19.6, 58.7], [21.5, 58.7], [23.7, 62.9], [20.8, 69.7], [20.8, 74.0], [22.0, 76.1], [23.7, 76.9], [31.8, 77.8], [33.3, 79.1], [39.7, 79.1], [42.3, 75.2], [44.5, 74.0], [55.0, 74.0], [58.6, 67.6]] },
    ],
  },
  {
    id: 'tundra-wide-2',
    theme: 'tundra',
    image: '/forests/tundra-wide-2.webp',
    tail: '/forests/tundra-wide-2-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.332,
    bottomAnchor: 0.65,
    home: { x: 34.9, y: 56.1 },
    pois: [
      { x: 47.4, y: 33.2, travelLength: 24, path: [[34.9, 56.1], [34.4, 50.2], [40.0, 41.7], [43.3, 40.4], [47.4, 33.2]] },
      { x: 80.6, y: 33.2, travelLength: 58, path: [[34.9, 56.1], [35.2, 59.5], [38.3, 65.0], [39.7, 65.9], [44.5, 65.9], [46.4, 68.0], [53.6, 68.0], [56.9, 65.5], [59.3, 65.5], [60.8, 62.9], [61.0, 59.9], [63.2, 56.1], [67.0, 54.4], [69.6, 49.7], [69.9, 45.5], [72.0, 41.7], [77.3, 39.1], [80.6, 33.2]] },
      { x: 21.8, y: 35.7, travelLength: 23, path: [[34.9, 56.1], [32.3, 51.0], [27.5, 50.6], [25.1, 46.3], [23.9, 40.8], [21.8, 35.7]] },
      { x: 49.0, y: 54.8, travelLength: 24, path: [[34.9, 56.1], [35.2, 59.5], [38.3, 65.0], [39.7, 65.9], [44.0, 65.9], [48.1, 58.7], [49.0, 54.8]] },
      { x: 77.3, y: 61.6, travelLength: 36, path: [[34.9, 56.1], [35.2, 59.5], [38.3, 65.0], [39.7, 65.9], [44.5, 65.9], [46.4, 68.0], [53.6, 68.0], [56.9, 65.5], [63.4, 65.5], [65.8, 66.7], [72.7, 66.7], [77.3, 61.6]] },
      { x: 24.6, y: 65.0, travelLength: 31, path: [[34.9, 56.1], [35.2, 59.9], [37.6, 67.2], [37.6, 71.4], [35.6, 74.0], [29.4, 74.8], [24.6, 65.0]] },
    ],
  },
  {
    id: 'tundra-wide-3',
    theme: 'tundra',
    image: '/forests/tundra-wide-3.webp',
    tail: '/forests/tundra-wide-3-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.302,
    bottomAnchor: 0.761,
    home: { x: 29.9, y: 57.0 },
    pois: [
      { x: 49.5, y: 30.2, travelLength: 42, path: [[29.9, 57.0], [29.9, 54.4], [27.3, 48.5], [28.0, 46.3], [32.1, 45.5], [34.0, 44.2], [41.9, 44.2], [46.2, 48.0], [49.0, 48.0], [50.2, 43.8], [50.2, 31.9], [49.5, 30.2]] },
      { x: 20.6, y: 32.3, travelLength: 25, path: [[29.9, 57.0], [29.9, 54.4], [20.6, 32.3]] },
      { x: 75.6, y: 41.2, travelLength: 49, path: [[29.9, 57.0], [29.9, 54.4], [27.3, 48.5], [27.5, 46.8], [28.9, 45.9], [30.9, 45.9], [34.0, 44.2], [41.9, 44.2], [46.2, 48.0], [55.7, 48.0], [62.4, 52.7], [69.6, 52.7], [75.6, 41.2]] },
      { x: 17.2, y: 71.8, travelLength: 34, path: [[29.9, 57.0], [29.9, 60.4], [28.5, 62.9], [27.5, 67.2], [28.0, 76.5], [26.6, 78.6], [24.4, 79.9], [21.3, 80.3], [17.2, 71.8]] },
      { x: 48.1, y: 74.8, travelLength: 41, path: [[29.9, 57.0], [29.9, 60.4], [28.5, 62.9], [27.5, 67.2], [27.5, 71.8], [29.9, 76.1], [37.6, 79.1], [40.7, 82.9], [42.8, 84.2], [45.9, 84.2], [47.4, 80.3], [47.4, 76.1], [48.1, 74.8]] },
      { x: 78.0, y: 76.1, travelLength: 63, path: [[29.9, 57.0], [29.9, 60.4], [28.5, 62.9], [27.5, 67.2], [27.5, 71.8], [30.9, 76.9], [33.3, 76.9], [37.6, 79.1], [40.7, 82.9], [42.8, 84.2], [47.1, 84.2], [48.3, 82.0], [58.9, 82.0], [59.8, 81.2], [62.4, 81.2], [66.0, 85.9], [70.3, 87.6], [72.5, 87.6], [78.0, 76.1]] },
    ],
  },
  {
    id: 'hell-wide-1',
    theme: 'hell',
    image: '/forests/hell-wide-1.webp',
    tail: '/forests/hell-wide-1-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.306,
    bottomAnchor: 0.659,
    home: { x: 18.2, y: 56.1 },
    pois: [
      { x: 39.2, y: 30.6, travelLength: 37, path: [[18.2, 56.1], [18.9, 54.4], [20.1, 54.4], [21.1, 52.7], [22.0, 52.7], [27.0, 42.9], [31.3, 42.9], [36.4, 45.9], [38.0, 45.9], [38.8, 43.8], [39.2, 30.6]] },
      { x: 14.1, y: 39.5, travelLength: 17, path: [[18.2, 56.1], [18.2, 52.7], [16.7, 50.2], [16.7, 47.2], [14.1, 39.5]] },
      { x: 89.2, y: 40.0, travelLength: 68, path: [[18.2, 56.1], [18.9, 54.4], [20.1, 54.4], [21.1, 52.7], [22.0, 52.7], [27.0, 42.9], [31.3, 42.9], [36.4, 45.9], [43.3, 45.9], [48.6, 49.7], [54.1, 49.7], [56.9, 53.1], [58.9, 54.0], [62.4, 55.3], [66.7, 55.3], [69.1, 53.1], [73.0, 47.2], [79.2, 47.2], [81.1, 50.2], [84.0, 50.6], [89.2, 40.0]] },
      { x: 58.4, y: 40.4, travelLength: 41, path: [[18.2, 56.1], [18.9, 54.4], [20.1, 54.4], [21.1, 52.7], [22.0, 52.7], [27.0, 42.9], [31.3, 42.9], [36.4, 45.9], [43.3, 45.9], [48.6, 49.7], [53.6, 49.7], [58.4, 40.4]] },
      { x: 29.7, y: 65.9, travelLength: 37, path: [[18.2, 56.1], [18.9, 54.4], [20.1, 54.8], [20.6, 58.7], [21.8, 60.8], [21.8, 65.5], [20.6, 70.1], [24.2, 75.7], [27.8, 76.9], [28.9, 74.8], [29.7, 72.3], [29.7, 65.9]] },
      { x: 56.7, y: 65.9, travelLength: 82, path: [[18.2, 56.1], [18.9, 54.4], [20.1, 54.4], [21.1, 52.7], [22.0, 52.7], [27.0, 42.9], [31.3, 42.9], [36.4, 45.9], [43.3, 45.9], [47.6, 49.3], [54.1, 49.7], [56.9, 53.1], [63.2, 55.3], [66.0, 60.4], [66.3, 63.3], [69.1, 69.3], [69.4, 73.1], [68.4, 74.8], [61.7, 78.2], [60.0, 79.9], [54.8, 79.9], [54.1, 78.6], [54.1, 72.7], [56.7, 65.9]] },
    ],
  },
  {
    id: 'hell-wide-2',
    theme: 'hell',
    image: '/forests/hell-wide-2.webp',
    tail: '/forests/hell-wide-2-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.272,
    bottomAnchor: 0.748,
    home: { x: 23.2, y: 57.4 },
    pois: [
      { x: 15.3, y: 27.2, travelLength: 32, path: [[23.2, 57.4], [22.5, 57.4], [20.1, 53.1], [18.4, 53.1], [15.8, 48.5], [15.8, 44.2], [17.9, 40.4], [17.9, 34.4], [15.3, 27.2]] },
      { x: 38.8, y: 30.6, travelLength: 35, path: [[23.2, 57.4], [24.4, 60.8], [26.6, 54.4], [29.9, 49.7], [33.7, 46.8], [37.1, 40.8], [38.0, 31.9], [38.8, 30.6]] },
      { x: 61.7, y: 36.6, travelLength: 47, path: [[23.2, 57.4], [24.4, 60.8], [25.6, 58.7], [25.6, 56.1], [29.9, 49.7], [34.7, 46.3], [36.6, 43.8], [40.7, 43.8], [42.6, 46.8], [45.7, 49.3], [51.7, 49.3], [55.0, 44.2], [57.9, 42.1], [61.7, 36.6]] },
      { x: 17.0, y: 69.7, travelLength: 32, path: [[23.2, 57.4], [27.0, 68.4], [27.3, 74.8], [24.6, 77.8], [20.8, 78.6], [17.0, 69.7]] },
      { x: 68.4, y: 70.6, travelLength: 57, path: [[23.2, 57.4], [25.6, 62.9], [27.0, 68.9], [28.7, 71.8], [32.5, 74.8], [35.6, 79.9], [38.8, 82.0], [40.9, 85.4], [43.5, 86.7], [49.8, 86.7], [56.0, 81.2], [62.0, 81.2], [64.1, 82.0], [68.4, 70.6]] },
      { x: 45.9, y: 74.8, travelLength: 35, path: [[23.2, 57.4], [25.6, 62.9], [27.0, 68.9], [28.7, 71.8], [32.5, 74.8], [37.3, 81.6], [41.9, 82.0], [45.9, 74.8]] },
    ],
  },
  {
    id: 'hell-wide-3',
    theme: 'hell',
    image: '/forests/hell-wide-3.webp',
    tail: '/forests/hell-wide-3-tail.webp',
    bleed: true,
    canvasAspect: '1672 / 941',
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    // v5.79: REAL trails traced from Javi's PC drawings (art/UI/mapspc).
    topAnchor: 0.259,
    bottomAnchor: 0.735,
    home: { x: 25.4, y: 52.3 },
    pois: [
      { x: 48.8, y: 25.9, travelLength: 40, path: [[25.4, 52.3], [26.1, 42.5], [27.5, 40.0], [30.6, 39.1], [32.8, 37.0], [36.6, 37.0], [40.7, 40.4], [45.7, 40.4], [47.1, 37.0], [47.8, 28.1], [48.8, 25.9]] },
      { x: 76.3, y: 27.6, travelLength: 61, path: [[25.4, 52.3], [26.1, 42.5], [27.5, 40.0], [30.6, 39.1], [32.8, 37.0], [36.6, 37.0], [40.7, 40.4], [45.2, 40.4], [47.4, 43.8], [51.2, 46.8], [62.4, 46.8], [63.4, 47.6], [67.5, 47.6], [71.5, 40.4], [73.7, 32.3], [76.3, 27.6]] },
      { x: 17.2, y: 28.1, travelLength: 25, path: [[25.4, 52.3], [25.6, 43.8], [23.0, 41.2], [20.8, 37.4], [17.2, 28.1]] },
      { x: 15.3, y: 59.1, travelLength: 40, path: [[25.4, 52.3], [24.9, 65.5], [25.8, 73.5], [24.9, 74.8], [21.3, 74.8], [18.9, 70.6], [16.5, 61.2], [15.3, 59.1]] },
      { x: 41.9, y: 72.3, travelLength: 65, path: [[25.4, 52.3], [25.1, 70.1], [25.8, 74.8], [28.2, 79.1], [35.9, 83.3], [43.8, 83.3], [46.2, 80.3], [50.2, 73.1], [50.2, 71.8], [45.5, 64.2], [41.9, 72.3]] },
      { x: 76.8, y: 73.5, travelLength: 70, path: [[25.4, 52.3], [26.1, 42.5], [27.5, 40.0], [30.6, 39.1], [32.8, 37.0], [36.6, 37.0], [40.7, 40.4], [45.2, 40.4], [47.4, 43.8], [51.2, 46.8], [60.3, 46.8], [62.2, 50.2], [63.2, 52.7], [63.4, 57.4], [60.3, 63.3], [60.0, 67.2], [60.8, 68.4], [72.5, 68.0], [76.8, 73.5]] },
    ],
  },
]

// v5.57: rand(), not Math.random — under a daily's per-node stream the
// painting (and so the POI layout) is part of the skeleton.
export function rollWideForestId(theme?: ForestTheme): string | null {
  const pool = theme ? WIDE_FORESTS.filter(f => f.theme === theme) : WIDE_FORESTS
  if (pool.length === 0) return null
  return pool[Math.floor(rand() * pool.length)]!.id
}

/** 🖥️ Landscape at node entry picks the wide set. */
export function isLandscapeViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth > window.innerHeight * 1.05
}

/** 🔄 v5.36 (per Javi): every portrait band map is PAIRED with a wide
 * map of its theme (by position within the theme's family, wrapping),
 * so a device rotation mid-node can swap the painting and re-snap the
 * missions onto the twin's pois. Deterministic — the same portrait map
 * always trades for the same wide map and back. Returns null for maps
 * outside the two families (base/extended canvases, boss thrones):
 * those never swap. 🧩 v6.28: the puzzle halls pair too (same six
 * pedestals in the same order); the boss arenas still don't — their
 * missions stand in board units, not on pois. */
export function pairedForestId(id: string, toLandscape: boolean): string | null {
  const hall = PUZZLE_HALLS.find(f => f.id === id)
  if (hall) return puzzleHallId(hall.theme, toLandscape)
  const from = toLandscape ? SQUISH_FORESTS : WIDE_FORESTS
  const to = toLandscape ? WIDE_FORESTS : SQUISH_FORESTS
  const cur = from.find(f => f.id === id)
  if (!cur) return null
  const family = from.filter(f => f.theme === cur.theme)
  const twins = to.filter(f => f.theme === cur.theme)
  if (twins.length === 0) return null
  const index = family.findIndex(f => f.id === id)
  const twin = twins[index % twins.length]!
  // The swap re-snaps missions by poi index — both templates carry six
  // sites today, but guard anyway so a future lopsided pair can't
  // strand markers.
  if (twin.pois.length !== cur.pois.length) return null
  return twin.id
}

export function rollForestId(theme?: ForestTheme): string {
  // 🧪 v4.51: squish test escape hatch (null since v5.35 — the band
  // composition WON and every biome has painted variants now).
  if (SQUISH_TEST_ID) return SQUISH_TEST_ID
  // 🗺️ v5.35: roll the one-screen-band family per theme first.
  const comp = theme ? SQUISH_FORESTS.filter(f => f.theme === theme) : SQUISH_FORESTS
  if (comp.length > 0) return comp[Math.floor(rand() * comp.length)]!.id
  // 🗺️ v2.60: the EXTENDED canvases are the field rotation — they carry
  // the terrain the banner and the roster sit on, so every node reads as
  // one continuous map. Originals stay resolvable (old saves, and any
  // biome without extended art yet) but aren't rolled.
  const ext = theme ? EXTENDED_FORESTS.filter(f => f.theme === theme) : EXTENDED_FORESTS
  if (ext.length > 0) return ext[Math.floor(rand() * ext.length)]!.id
  const pool = theme ? FORESTS.filter(f => f.theme === theme) : FORESTS
  const use = pool.length > 0 ? pool : FORESTS
  return use[Math.floor(rand() * use.length)]!.id
}

/**
 * Total trail length in percent units (x weighted by the 1:2 portrait
 * aspect). Travel time scales with this, so a winding trail takes honestly
 * longer than a site that happens to sit close as the crow flies.
 */
export function poiTravelLength(poi: ForestPoi | undefined): number {
  if (!poi) return 0
  return poi.travelLength ?? pathLength(poi.path)
}

export function pathLength(path: [number, number][]): number {
  let total = 0
  for (let i = 1; i < path.length; i++) {
    const dx = (path[i]![0] - path[i - 1]![0]) * FOREST_ASPECT
    const dy = path[i]![1] - path[i - 1]![1]
    total += Math.hypot(dx, dy)
  }
  return total
}

/**
 * Point along a trail at parameter t (0 = home, 1 = site), interpolated
 * by arc length so walkers cover ground evenly. The x axis is weighted
 * by FOREST_ASPECT in the length metric (1% of x is a shorter stroll
 * than 1% of y on a portrait canvas).
 */
/** ⚡ v4.91 (perf review #2): trail paths are static data, but the
 * board asks for points along them ~1,000×/sec while parties march —
 * the arc-length table was being rebuilt on every call. Cache it per
 * path array. */
const ARC_CACHE = new WeakMap<[number, number][], { lengths: number[]; total: number }>()

export function pointAlongPath(path: [number, number][], t: number): { x: number; y: number } {
  if (path.length === 0) return { x: 50, y: 50 }
  if (path.length === 1 || t <= 0) return { x: path[0]![0], y: path[0]![1] }
  if (t >= 1) return { x: path[path.length - 1]![0], y: path[path.length - 1]![1] }
  let arc = ARC_CACHE.get(path)
  if (!arc) {
    const lengths: number[] = []
    let total = 0
    for (let i = 1; i < path.length; i++) {
      const dx = (path[i]![0] - path[i - 1]![0]) * FOREST_ASPECT
      const dy = path[i]![1] - path[i - 1]![1]
      const len = Math.hypot(dx, dy)
      lengths.push(len)
      total += len
    }
    arc = { lengths, total }
    ARC_CACHE.set(path, arc)
  }
  const { lengths, total } = arc
  let target = t * total
  for (let i = 0; i < lengths.length; i++) {
    if (target <= lengths[i]! || i === lengths.length - 1) {
      const f = lengths[i]! > 0 ? Math.min(1, target / lengths[i]!) : 1
      return {
        x: path[i]![0] + (path[i + 1]![0] - path[i]![0]) * f,
        y: path[i]![1] + (path[i + 1]![1] - path[i]![1]) * f,
      }
    }
    target -= lengths[i]!
  }
  return { x: path[path.length - 1]![0], y: path[path.length - 1]![1] }
}
